// VRCPro.jsx — Valor Real Competitivo PRO
// Transformación completa del HTML VRC_BY_Raices_v12_PRO al ecosistema React
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import './VRCPro.css';
import logo from '../../../Assets/nuevoLogo.png'
import { buildVRCPayload, buildVRCFormData } from './buildVRCPayload';
import { validateVRCInputs, getVRCFieldLabel } from './validateVRCPro';
import ClientSelector from '../../../Components/ClientSelector/ClientSelector';
import {
  createVRC as createVRCAction,
  updateVRC as updateVRCAction,
  fetchVRCById as fetchVRCByIdAction,
  clearVRCDetail as clearVRCDetailAction,
  getClientsBySeller as getClientsBySellerAction,
} from '../../../Redux/Actions/actions';

// ─── CONSTANTES ──────────────────────────────────────────────────

// Corredora Pública Inmobiliaria responsable de la inmobiliaria.
// Su firma + matrícula actúa como resguardo legal en portada, footer y descargo del VRC.
const CORREDORA_RESPONSABLE = {
  name: 'Julieta Garcia',
  matricula: 'C.C.P.I.M. 2009',
  rol: 'Corredora Pública Inmobiliaria Responsable',
};

const AGENTS = [
  { name: 'Martín Atencia', role: 'Líder Captación y Ventas', initials: 'MA' },
  { name: 'Agustín Rosa', role: 'Socio / Director', initials: 'AR' },
  { name: 'Tamara Sanesteban', role: 'Agente', initials: 'TS' },
  { name: 'Micaela Gonzella', role: 'Agente', initials: 'MG' },
  { name: 'Denise Sanesteban', role: 'Agente', initials: 'DS' },
];

const AMENITIES_LIST = [
  { id: 'pileta', label: '🏊 Pileta' },
  { id: 'quincho', label: '🔥 Quincho / asador' },
  { id: 'jardin', label: '🌿 Jardín' },
  { id: 'garage_techado', label: '🚗 Garage techado' },
  { id: 'seguridad_24h', label: '🛡️ Seguridad 24 h' },
  { id: 'sum', label: '🏢 SUM' },
  { id: 'gimnasio', label: '🏋️ Gimnasio' },
  { id: 'laundry', label: '🧺 Laundry' },
  { id: 'terraza', label: '🌇 Terraza' },
  { id: 'calefaccion_central', label: '♨️ Calefacción central' },
  { id: 'aire_central', label: '❄️ Aire acondicionado central' },
  { id: 'parrilla', label: '🔥 Parrilla' },
];

const AMENITIES_COMP = AMENITIES_LIST;

const GOOGLE_MAPS_API_KEY = 'AIzaSyD5V2B-G8s7P7Hh6cZ6UyucD0n91y-JI3I';
const ESTADO_OPTIONS = ['1.00', '0.97', '0.95', '0.92', '0.90', '0.87', '0.85', '0.80', '0.70', '0.50'];
const ANT_OPTIONS = ['1.00', '0.97', '0.95', '0.92', '0.90', '0.87', '0.85', '0.78', '0.70'];
const NEG_OPTIONS = ['1.00', '0.98', '0.96', '0.94', '0.92', '0.90', '0.85'];
const ACCESO_OPTIONS = ['1.05', '1.02', '1.00', '0.98', '0.96', '0.94', '0.92', '0.88'];

// Honorarios por defecto y pasos del Plan de Acción BY Raíces (página final del PDF).
const HONORARIOS_DEFAULT = '3% + IVA';
const PLAN_ACCION_PASOS = [
  { t: 'Documentación', d: 'Recopilamos y verificamos títulos, planos, libre deuda municipal e impuestos; revisamos situación dominial y registral del inmueble.' },
  { t: 'Fotografía profesional', d: 'Sesión fotográfica con equipo profesional, drone si corresponde y postproducción. Tour virtual 360° opcional.' },
  { t: 'Publicación multicanal', d: 'Argenprop, ZonaProp, MercadoLibre, Inmuebles24, redes sociales y red BY Raíces. Cartelía física con destacado.' },
  { t: 'Seguimiento activo', d: 'Gestión de consultas, agendamiento de visitas y reporte semanal de métricas (vistas, contactos, ofertas) al propietario.' },
  { t: 'Negociación', d: 'Acompañamiento profesional en cada oferta, análisis de contraofertas y resguardo del valor real competitivo del inmueble.' },
  { t: 'Cierre y escritura', d: 'Coordinación con escribanía, redacción de boletín de reserva, sena y firma. Acompañamiento hasta la posesión efectiva.' },
];

// Código VRC autoincremental por año, persistido en localStorage.
// Formato: VRC-AAAA-NNNN (NNNN con padding de 4 dígitos).
function generateVRCCode() {
  const year = new Date().getFullYear();
  const key = `vrc_counter_${year}`;
  let next = 1;
  try {
    const curr = parseInt(localStorage.getItem(key) || '0', 10);
    next = (isNaN(curr) ? 0 : curr) + 1;
    localStorage.setItem(key, String(next));
  } catch { /* localStorage bloqueado: usar timestamp */
    next = Math.floor((Date.now() / 1000) % 10000);
  }
  return `VRC-${year}-${String(next).padStart(4, '0')}`;
}

const getAntiguedadCoef = (years) => {
  const y = parseFloat(years) || 0;
  if (y < 5) return '1.00';
  if (y < 8) return '0.97';
  if (y < 10) return '0.95';
  if (y < 15) return '0.92';
  if (y < 20) return '0.90';
  if (y < 25) return '0.87';
  if (y < 30) return '0.85';
  if (y < 40) return '0.78';
  return '0.70';
};

const resolveSelectValue = (value, customValue, options) => {
  if (customValue) return 'custom';
  return options.includes(String(value)) ? String(value) : 'custom';
};

const getLoggedUserName = (userDetails, user) => {
  const sources = [userDetails, user?.user, user].filter(Boolean);
  for (const src of sources) {
    const full = [src?.name, src?.last_name].filter(Boolean).join(' ').trim();
    if (full) return full;
    if (src?.full_name) return String(src.full_name).trim();
    if (src?.seller_name) return String(src.seller_name).trim();
    if (src?.username) return String(src.username).trim();
    if (src?.email) return String(src.email).split('@')[0];
  }
  return '';
};

const normalizeStoredResultado = (stored = {}) => {
  const vrc = Number(stored.vrcFinal ?? stored.vrcCalculado ?? stored.vrc ?? 0) || 0;
  const vus = (stored.vusPorComparable || stored.vus || []).map(Number).filter(Number.isFinite);
  return {
    ...stored,
    vrc,
    vus,
    vuPromedio: Number(stored.vuPromedio ?? 0) || 0,
    rangoMin: Number(stored.rangoMin ?? vrc * 0.95) || 0,
    rangoMax: Number(stored.rangoMax ?? vrc * 1.05) || 0,
    supHomProp: Number(stored.supHomProp ?? stored.supHomogeneizadaProp ?? 0) || 0,
    rhFactor: Number(stored.rhFactor ?? stored.obsfCoef ?? 1) || 1,
    hnCoef: Number(stored.hnCoef ?? stored.hofmannNeillCoef ?? 1) || 1,
    pisoCoef: Number(stored.pisoCoef ?? 1) || 1,
    amPropCoef: Number(stored.amPropCoef ?? 1) || 1,
    oriCoef: Number(stored.oriCoef ?? 1) || 1,
    catPropCoef: Number(stored.catPropCoef ?? 1) || 1,
    obsfCoef: Number(stored.obsfCoef ?? 1) || 1,
    baseMercado: Number(stored.baseMercado ?? 0) || 0,
    ajusteRaw: Number(stored.ajusteRaw ?? 1) || 1,
    ajusteClamped: Number(stored.ajusteClamped ?? stored.ajusteRaw ?? 1) || 1,
    clamped: Boolean(stored.clamped),
    zonaLabel: stored.zonaLabel || '',
  };
};

// ─── CÁLCULOS ────────────────────────────────────────────────────────────────

export function calcSupHom(cub, semi, coefSemi, ter, coefTer) {
  const cubierta = parseFloat(cub) || 0;
  const semicubierta = parseFloat(semi) || 0;
  const terreno = parseFloat(ter) || 0;
  const coefSemicubierta = parseFloat(coefSemi) || 0.5;
  const coefTerreno = parseFloat(coefTer) || 0.5;

  return (
    cubierta +
    semicubierta * coefSemicubierta +
    Math.max(0, terreno - cubierta) * coefTerreno
  );
}

export function calcAmCoef(selectedAmenities) {
  // Los amenities se informan en el análisis, pero no reciben un porcentaje
  // universal: su impacto depende del mercado y de la comparación elegida.
  return 1;
}

export function calcVU(precio, supHom, estado, antCoef, neg, tiempo, ubic, cat, acceso, amenities) {
  if (!precio || !supHom) return 0;
  const vu = (precio / supHom)
    * parseFloat(estado)
    * parseFloat(antCoef)
    * parseFloat(neg)
    * parseFloat(tiempo)
    * parseFloat(ubic)
    * (1 / parseFloat(cat))
    * parseFloat(acceso)
    * calcAmCoef(amenities);
  return vu;
}

// Vida útil de referencia por tipo de inmueble (años).
// Terreno: depreciación mínima (se usa un valor muy alto en lugar de ∞
// para mantener compatibilidad con la fórmula polinómica).
export function vidaUtilEstimada(tipo) {
  if (tipo === 't') return 999;
  if (tipo === 'local') return 55;
  if (tipo === 'dpto' || tipo === 'depto' || tipo === 'departamento') return 65;
  return 70;
}

// Ross-Heidecke polinómica (HTML v12): 1 − 1.0646x + 0.7194x² − 0.4588x³
// donde x = antigüedad_efectiva / vida_útil (clamped 0–1).
export function getRossHeideckeFactor(tipo, antiguedad, estado, reciclada) {
  const vidaUtil = vidaUtilEstimada(tipo);
  if (vidaUtil <= 0) return 1;
  let antEfectiva = parseFloat(antiguedad) || 0;
  if (reciclada) antEfectiva = Math.max(0, antEfectiva - 10);
  const x = Math.min(antEfectiva / vidaUtil, 1);
  const factor = 1 - 1.0646 * x + 0.7194 * x * x - 0.4588 * x * x * x;
  return Math.max(0, Math.min(1, factor));
}

// Coeficiente por antigüedad de la publicación del comparable.
// HTML v12: <1m=1, <=3m=1.005, <=6m=1.01, <=12m=1.02, >12m=1.03.
export function fechaCoef(fechaPub) {
  if (!fechaPub) return 1;
  const pub = new Date(fechaPub + 'T12:00');
  if (isNaN(pub.getTime())) return 1;
  const months = Math.max(0, (Date.now() - pub.getTime()) / (1000 * 60 * 60 * 24 * 30));
  if (months <= 1) return 1;
  if (months <= 3) return 1.005;
  if (months <= 6) return 1.01;
  if (months <= 12) return 1.02;
  return 1.03;
}

// Rango VU referencial por zona de Mendoza (USD/m²).
export function rangoPrecioMendoza(zona, tipo) {
  const z = (zona || '').toLowerCase();
  const t = (tipo || '').toLowerCase();
  const esTerreno = t.includes('terreno') || t.includes('lote');
  if (esTerreno) {
    if (z.includes('capital') || z.includes('godoy')) return { min: 400, max: 900, label: 'Capital/Godoy Cruz' };
    if (z.includes('las heras') || z.includes('guaymallén') || z.includes('guaymallen')) return { min: 250, max: 600, label: 'Las Heras/Guaymallén' };
    if (z === 'rural' || z.includes('rural')) return { min: 30, max: 200, label: 'Rural' };
    if (z === 'comercial' || z.includes('comercial')) return { min: 350, max: 2500, label: 'Comercial' };
    return { min: 120, max: 800, label: 'Gran Mendoza' };
  }
  if (z.includes('capital')) return { min: 1200, max: 2500, label: 'Capital' };
  if (z.includes('godoy')) return { min: 900, max: 2000, label: 'Godoy Cruz' };
  if (z.includes('las heras')) return { min: 700, max: 1600, label: 'Las Heras' };
  if (z.includes('luján') || z.includes('lujan') || z.includes('chacras')) return { min: 1000, max: 2200, label: 'Luján/Chacras' };
  if (z === 'comercial' || z.includes('comercial')) return { min: 500, max: 3500, label: 'Comercial' };
  return { min: 450, max: 2600, label: 'Gran Mendoza' };
}

// Validación simple: warnings sobre datos potencialmente inconsistentes.
export function validarVU(antiguedad, vidaUtil) {
  const msgs = [];
  const a = parseFloat(antiguedad) || 0;
  const vu = parseFloat(vidaUtil) || 0;
  if (vu > 0 && a > vu) {
    msgs.push(`La antigüedad (${a} años) supera la vida útil estimada (${vu} años).`);
  }
  return msgs;
}

export function calcHofmannNeill(frenteReal, frenteTipo) {
  const fr = parseFloat(frenteReal) || 0;
  const ft = parseFloat(frenteTipo) || 10;
  if (!fr || !ft) return 1;
  return Math.sqrt(fr / ft);
}

export function getRangoVU(zona, tipo) {
  const z = (zona || '').toLowerCase();
  const t = (tipo || '').toLowerCase();
  if (t.includes('terreno') || t.includes('lote')) {
    if (z === 'comercial') return [350, 2500];
    if (z === 'rural') return [30, 200];
    return [120, 800];
  }
  if (z === 'comercial') return [500, 3500];
  return [450, 2600];
}

// Uplift de presentación (legacy): se mantiene exportado por compatibilidad,
// pero el cálculo nuevo NO lo usa. El método argentino estándar centra el VRC
// en el valor comparativo de mercado (VU × SupHom) y aplica los ajustes
// técnicos como un único factor acotado a ±15% (ver CLAMP_BAND).
export const VRC_PRESENTATION_UPLIFT = 1.075;

// Tope de ajuste técnico sobre la base comparativa.
// Garantiza que los coeficientes propios (orientación, categoría, Hofmann-Neill,
// piso, obsf, amenities, Ross-Heidecke) NUNCA muevan el VRC más de ±15% del
// valor comparativo de mercado. Es la práctica habitual en tasación residencial
// argentina: el mercado manda, los coef. afinan.
export const CLAMP_BAND = 0.15; // ±15%

// Margen de publicación: precio para publicar = VRC × (1 + PUBLISH_MARGIN).
export const PUBLISH_MARGIN = 0.05; // +5%

export function roundToNice(n) {
  const x = Math.max(0, Number(n) || 0);
  if (x < 50_000)  return Math.round(x / 1_000)  * 1_000;
  if (x < 500_000) return Math.round(x / 5_000)  * 5_000;
  return Math.round(x / 10_000) * 10_000;
}

export function calcVRC(data) {
  const {
    tipo, supCub, supSemi, coefSemi, supTer, coefTer,
    antiguedad, reciclada, ori, catProp, obsf,
    estado: estadoProp,
    frenteReal, frenteTipo, piso,
    amenitiesProp, zona,
    comparables,
    manualVRC,
    esTerreno,
    // Coef. propios del terreno (1.00 = referencia)
    tTopoProp = '1', tSvcCoefProp = '1', tFormaProp = '1', tHabProp = '1',
  } = data;

  if (manualVRC) {
    const mv = roundToNice(manualVRC);
    return {
      vrc: mv,
      vus: [],
      rangoMin: roundToNice(mv * 0.95),
      rangoMax: roundToNice(mv * 1.05),
      zonaLabel: 'Ajuste manual',
    };
  }

  // SUPERFICIE HOMOGENEIZADA DEL INMUEBLE PROPIO
  // ─ Terreno: la sup. principal es supTer puro (sin coef. de homogeneización).
  // ─ Edificado: cubierta + semicubierta ponderada + terreno excedente
  //   respecto de la huella cubierta. El terreno completo no se vuelve a
  //   contar cuando ya está representado por la superficie construida.
  const supHomProp = esTerreno
    ? (parseFloat(supTer) || 0)
    : calcSupHom(supCub, supSemi, coefSemi, supTer, coefTer);
  if (!supHomProp) return null;

  const compActivos = comparables.filter(c => parseFloat(c.precio) > 0 && (parseFloat(c.supCub) > 0 || parseFloat(c.supTer) > 0));
  if (compActivos.length === 0) return null;

  // VU ajustado de cada comparable.
  // ─ Terreno: supHom = supTer puro; se neutralizan coef. constructivos
  //   (estado, antCoef, cat, amenities) que no aplican a un lote vacío.
  //   Se aplican coef. específicos del lote (topo, servicios, forma, zonif)
  //   como ratio propio/comparable para llevar el VU del comparable al
  //   equivalente del lote propio.
  // ─ Edificado: lógica clásica con todos los coef. multiplicativos.
  const num = (v, def = 1) => {
    const n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : def;
  };
  const vusData = compActivos.map(c => {
    const supHom = esTerreno
      ? (parseFloat(c.supTer) || 0)
      : calcSupHom(c.supCub, c.supSemi, 0.5, c.supTer, 0.5);
    const estadoEff = esTerreno ? 1 : c.estado;
    const antEff = esTerreno ? 1 : c.antCoef;
    const catEff = esTerreno ? 1 : c.cat;
    const amEff = esTerreno ? [] : c.amenities;
    const vuBase = calcVU(c.precio, supHom, estadoEff, antEff, c.neg, c.tiempo, c.ubic, catEff, c.acceso, amEff);
    // Ratio terreno: convierte el VU del comparable al "equivalente propio"
    // multiplicando por (propio / comparable) de cada característica del lote.
    const ratioLote = esTerreno
      ? (num(tTopoProp) / num(c.tTopo))
        * (num(tSvcCoefProp) / num(c.tSvcCoef))
        * (num(tFormaProp) / num(c.tForma))
        * (num(tHabProp) / num(c.tZonif))
      : 1;
    const vu = vuBase * fechaCoef(c.fechaPub) * ratioLote;
    return { vu, supHom };
  }).filter(v => v.vu > 0);

  if (!vusData.length) return null;

  const vus = vusData.map(v => v.vu);

  // VU promedio ponderado por similitud al SupHom del inmueble: los comparables
  // con superficie más parecida pesan más (núcleo del método comparativo).
  const totalPeso = vusData.reduce((acc, v) => {
    const ratio = Math.min(v.supHom, supHomProp) / Math.max(v.supHom, supHomProp);
    return acc + Math.max(0.5, ratio); // piso 0.5 para que un comparable muy distinto siga contando
  }, 0);
  const vuPromedio = totalPeso > 0
    ? vusData.reduce((acc, v) => {
        const ratio = Math.min(v.supHom, supHomProp) / Math.max(v.supHom, supHomProp);
        return acc + v.vu * Math.max(0.5, ratio);
      }, 0) / totalPeso
    : vusData.reduce((a, b) => a + b.vu, 0) / vusData.length;

  // BASE DE MERCADO: núcleo del valor. El comparativo domina.
  const baseMercado = vuPromedio * supHomProp;

  // Coeficientes propios del inmueble (afinan el valor, no lo dominan).
  // ─ Terreno: la mayoría no aplica. Solo Hofmann-Neill (frente) tiene sentido
  //   en lotes urbanos típicos; el resto se neutraliza a 1.
  const rhFactor = esTerreno ? 1 : getRossHeideckeFactor(tipo, antiguedad, null, reciclada);
  const hnCoef = calcHofmannNeill(frenteReal, frenteTipo);
  const pisoCoef = esTerreno ? 1 : 1 + (parseFloat(piso) || 0) * 0.015;
  const amPropCoef = esTerreno ? 1 : calcAmCoef(amenitiesProp);
  const oriCoef = esTerreno ? 1 : parseFloat(ori || 1);
  const catPropCoef = esTerreno ? 1 : parseFloat(catProp || 1);
  const obsfCoef = esTerreno ? 1 : parseFloat(obsf || 1);
  // Estado del inmueble propio: afina el ajuste técnico (quedó dentro
  // del clamp ±CLAMP_BAND, así que un estado "Pésima (0.50)" no destruye
  // el VRC, solo lo empuja al piso de -15%).
  const estadoPropCoef = esTerreno ? 1 : parseFloat(estadoProp || 1);

  // Producto bruto de ajustes propios. Puede ser p.ej. 0.65 o 1.42.
  const ajusteRaw = oriCoef * catPropCoef * hnCoef * pisoCoef * obsfCoef * amPropCoef * rhFactor * estadoPropCoef;

  // Acotamos el ajuste técnico a ±CLAMP_BAND (15%) sobre la base de mercado.
  // Esto evita que la cascada de coeficientes "dispare" o "mate" el VRC.
  const minBand = 1 - CLAMP_BAND;
  const maxBand = 1 + CLAMP_BAND;
  const ajusteClamped = Math.max(minBand, Math.min(maxBand, ajusteRaw));
  const clamped = ajusteRaw !== ajusteClamped;

  const vrcRaw = baseMercado * ajusteClamped;
  const vrc = roundToNice(vrcRaw);
  const rangoMin = roundToNice(vrcRaw * 0.95);
  const rangoMax = roundToNice(vrcRaw * 1.05);

  const [vuMin, vuMax] = getRangoVU(zona, tipo);
  const zonaLabel = vuPromedio < vuMin
    ? '⚠️ VU por debajo del rango esperado'
    : vuPromedio > vuMax
    ? '⚠️ VU por encima del rango esperado'
    : '✅ VU dentro del rango esperado';

  return {
    vrc, vus, vuPromedio, rangoMin, rangoMax, zonaLabel, supHomProp, rhFactor, hnCoef,
    pisoCoef, amPropCoef,
    oriCoef, catPropCoef, obsfCoef, estadoPropCoef,
    vrcRaw,
    baseMercado,
    ajusteRaw,
    ajusteClamped,
    clamped,
  };
}

// ─── ESTADO INICIAL DE COMPARABLE ────────────────────────────────────────────

function initComp() {
  return {
    dir: '', precio: '', supCub: '', supSemi: '', supTer: '',
    ant: '', dorm: '', ban: '',
    estado: '0.90', antCoef: '0.70', neg: '0.94', tiempo: '1.00',
    ubic: '1.00', cat: '1.00', acceso: '1.00',
    amenities: [],
    link: '', venta: 'En venta', fechaPub: '', obs: '',
    fotos: [],
    // custom inputs
    estadoCustom: '', antCoefCustom: '', negCustom: '', accesoCustom: '',
    // ── Terreno / Lote ─────────────────────────────────────────
    tFrente: '', tFondo: '',
    tTopo: '1.00',     // 1=plano, <1=pendiente/irregular
    tSvcCoef: '1.00',  // 1=todos los servicios; <1=faltantes
    tForma: '1.00',    // 1=regular; >1 esquina premium; <1 irregular
    tZonif: '1.00',    // 1=residencial estándar
  };
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const fmt = (n) => {
  if (!n && n !== 0) return '—';
  return 'USD ' + Math.round(n).toLocaleString('es-AR');
};

const fmtN = (n, dec = 0) => {
  if (!n && n !== 0) return '—';
  return parseFloat(n).toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

// ─── HELPER: MAPA COMO BASE64 PARA PDF ──────────────────────────────────────
// Prioridad 1: Google Static Maps via images.weserv.nl (CDN con CORS nativo)
// Prioridad 2: corsproxy.io / allorigins como respaldo
// Prioridad 3: tiles OSM propios (CORS nativo, sin proxies, siempre funciona)

async function fetchMapAsBase64(lat, lng) {
  // Ratio 16:9 (640x360 @ scale=2 = 1280x720) — coincide con el contenedor final
  // del PDF/pantalla, evita que object-fit:cover recorte agresivo y el pin se vea chico.
  const staticUrl =
    `https://maps.googleapis.com/maps/api/staticmap` +
    `?center=${lat},${lng}&zoom=16&size=640x360&scale=2&maptype=roadmap` +
    `&markers=size:mid%7Ccolor:0x3b3fb5%7Clabel:%E2%97%8F%7C${lat},${lng}` +
    `&style=feature:poi%7Cvisibility:off` +
    `&style=feature:transit%7Cvisibility:off` +
    `&style=feature:road.arterial%7Celement:labels%7Cvisibility:simplified` +
    `&key=${GOOGLE_MAPS_API_KEY}`;

  // images.weserv.nl: CDN especializado en proxy de imágenes, CORS nativo, caché global
  const proxies = [
    `https://images.weserv.nl/?url=${encodeURIComponent(staticUrl.replace('https://', ''))}`,
    `https://corsproxy.io/?${encodeURIComponent(staticUrl)}`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(staticUrl)}`,
  ];

  for (const proxyUrl of proxies) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 8000);
      const r = await fetch(proxyUrl, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!r.ok) continue;
      // Verificar que el proxy devolvió una imagen y no una página HTML de error
      const ct = r.headers.get('content-type') || '';
      if (!ct.startsWith('image/')) continue;
      const blob = await r.blob();
      const base64 = await new Promise(res => {
        const fr = new FileReader();
        fr.onloadend = () => res(fr.result);
        fr.onerror = () => res(null);
        fr.readAsDataURL(blob);
      });
      if (base64 && base64.startsWith('data:image')) return base64;
    } catch { /* intentar siguiente proxy */ }
  }

  // Todos los proxies fallaron → tiles OSM (siempre funciona)
  return buildMapFromOSM(lat, lng);
}

async function buildMapFromOSM(lat, lng) {
  const zoom = 16;
  const n = Math.pow(2, zoom);
  const cx = (lng + 180) / 360 * n;
  const latRad = lat * Math.PI / 180;
  const cy = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  const tileX = Math.floor(cx);
  const tileY = Math.floor(cy);
  const offsetX = (cx - tileX) * 256;
  const offsetY = (cy - tileY) * 256;

  const T = 256;
  const W = 1280, H = 720; // ratio 16:9 alineado al contenedor final
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e8e0d8';
  ctx.fillRect(0, 0, W, H);

  const tlX = offsetX - W / 2;
  const tlY = offsetY - H / 2;
  const startTX = Math.floor(tileX + tlX / T);
  const startTY = Math.floor(tileY + tlY / T);
  const endTX = Math.ceil(tileX + (tlX + W) / T);
  const endTY = Math.ceil(tileY + (tlY + H) / T);

  const loads = [];
  for (let ty = startTY; ty < endTY; ty++) {
    for (let tx = startTX; tx < endTX; tx++) {
      const canvasX = Math.round((tx - tileX) * T - tlX);
      const canvasY = Math.round((ty - tileY) * T - tlY);
      // IMPORTANTE: sin headers — User-Agent está prohibido en fetch() del browser
      loads.push(
        fetch(`https://tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`)
          .then(r => r.ok ? r.blob() : Promise.reject())
          .then(blob => new Promise(res => {
            const img = new Image();
            const objUrl = URL.createObjectURL(blob);
            img.onload = () => { ctx.drawImage(img, canvasX, canvasY); URL.revokeObjectURL(objUrl); res(); };
            img.onerror = () => { URL.revokeObjectURL(objUrl); res(); };
            img.src = objUrl;
          }))
          .catch(() => {})
      );
    }
  }
  await Promise.all(loads);

  // Pin de ubicación (escalado para canvas 1280x720)
  const mx = W / 2, my = H / 2;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 6;
  ctx.fillStyle = '#3b3fb5';
  ctx.beginPath();
  ctx.arc(mx, my - 38, 28, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(mx, my - 38, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#3b3fb5';
  ctx.beginPath();
  ctx.moveTo(mx - 18, my - 32);
  ctx.lineTo(mx + 18, my - 32);
  ctx.lineTo(mx, my);
  ctx.closePath();
  ctx.fill();

  return canvas.toDataURL('image/jpeg', 0.92);
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

export default function VRCPro() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const editId = params?.id || null;
  const isEditMode = !!editId;

  const [step, setStep] = useState(1);
  const [showInstr, setShowInstr] = useState(false);
  const [showComp3, setShowComp3] = useState(false);
  const [viewMode, setViewMode] = useState('agent'); // 'agent' | 'prop'
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfMode, setPdfMode] = useState(false);
  const [pdfMapUrl, setPdfMapUrl] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const resultRef = useRef(null);
  const coverRef = useRef(null);
  const page2Ref = useRef(null);
  const page3Ref = useRef(null);
  const page4Ref = useRef(null);
  const page5Ref = useRef(null);
  const page6Ref = useRef(null);
  // Refs de las 4 páginas del informe propietario (PDF limpio).
  const propPage1Ref = useRef(null);
  const propPage2Ref = useRef(null);
  const propPage3Ref = useRef(null);
  const propPage4Ref = useRef(null);
  const mapCaptureRef = useRef(null); // captura live del GoogleMap antes de pdfMode
  const mapImgRef = useRef(null);     // ref para la <img> del Static Maps en pdfMode
  const chartRef = useRef(null);      // canvas del chart comparativo

  // ── Usuario logeado
  const user = useSelector(state => state.user);
  const userDetails = useSelector(state => state.userDetails);
  const sellerIdFromStore = useSelector(state => state.userId);
  const vrcDetail = useSelector(state => state.vrcDetail);
  const clientsBySeller = useSelector(state => state.clientsBySeller || []);

  // ── Persistencia (guardar en backend)
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedId, setSavedId] = useState(null);
  // Errores de validación del cálculo
  const [calcError, setCalcError] = useState(null);
  const [calcWarnings, setCalcWarnings] = useState([]);
  // Mapa { field: message } con los errores por campo del validador estructurado.
  // Permite resaltar los inputs con borde rojo (mismo patrón que CreateProperty).
  const [fieldErrors, setFieldErrors] = useState({});
  const [calcErrorsList, setCalcErrorsList] = useState([]);
  // Fotos eliminadas en modo edición (publicIds)
  const [fotosEliminadas, setFotosEliminadas] = useState([]);
  // Cliente asociado (opcional)
  const [clientId, setClientId] = useState('');
  // Ref al último nombre auto-cargado por ClientSelector. Si el `propietario`
  // actual coincide con este valor, podemos sobrescribirlo al cambiar de
  // cliente sin pisar ediciones manuales del usuario.
  const propietarioRef = useRef('');

  // Lista que pasamos a ClientSelector: array con el cliente vinculado (o vacío).
  // Derivado del clientId + catálogo cargado por el selector. Si el catálogo
  // aún no llegó (modo edición) usamos un stub con sólo el id.
  const selectedClients = useMemo(() => {
    if (!clientId) return [];
    const found = clientsBySeller.find(c => String(c.id) === String(clientId));
    return found ? [found] : [{ id: clientId }];
  }, [clientId, clientsBySeller]);

  // ── Estado inmueble
  // Init directo: user/userDetails ya están disponibles antes del useState
  const [agente, setAgente] = useState(() => getLoggedUserName(userDetails, user) || '');

  // Auto-poblar agente cuando userDetails llega de forma asíncrona (después del mount)
  useEffect(() => {
    const nombre = getLoggedUserName(userDetails, user);
    if (nombre) setAgente(prev => prev || nombre);
  }, [user, userDetails]);

  // Cargar clientes del seller (autocomplete opcional en step 1)
  useEffect(() => {
    const sellerId = localStorage.getItem('userId');
    if (sellerId) {
      dispatch(getClientsBySellerAction(sellerId)).catch(() => {});
    }
  }, [dispatch]);

  // Modo edición: traer VRC e hidratar estado local
  useEffect(() => {
    if (!isEditMode) return;
    dispatch(fetchVRCByIdAction(editId)).catch(err => {
      setSaveError(err?.response?.data?.message || err.message || 'No se pudo cargar el VRC');
    });
    return () => { dispatch(clearVRCDetailAction()); };
  }, [dispatch, editId, isEditMode]);
  const [propietario, setPropietario] = useState('');
  const [tipoInm, setTipoInm] = useState('e'); // 'e' edificado | 't' terreno
  const [tipo, setTipo] = useState('');
  const [dir, setDir] = useState('');
  const [zona, setZona] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().split('T')[0]);
  const [supCub, setSupCub] = useState('');
  const [supSemi, setSupSemi] = useState('');
  const [coefSemi, setCoefSemi] = useState('0.50');
  const [supTer, setSupTer] = useState('');
  const [coefTer, setCoefTer] = useState('0.50');
  const [antiguedad, setAntiguedad] = useState('');
  const [reciclada, setReciclada] = useState(false);
  const [dorm, setDorm] = useState('');
  const [ban, setBan] = useState('');
  const [coch, setCoch] = useState('');
  const [piso, setPiso] = useState('');
  const [ori, setOri] = useState('1');
  const [estado, setEstado] = useState('0.90');
  const [estadoCustom, setEstadoCustom] = useState('');
  const [obsf, setObsf] = useState('1');
  const [svc, setSvc] = useState('');
  const [frenteReal, setFrenteReal] = useState('');
  const [frenteTipo, setFrenteTipo] = useState('10');
  const [catProp, setCatProp] = useState('1.00');
  const [amenitiesProp, setAmenitiesProp] = useState([]);
  // Terreno
  const [tSup, setTSup] = useState('');
  const [tFr, setTFr] = useState('');
  const [tFo, setTFo] = useState('');
  const [tTopo, setTTopo] = useState('1.00');
  const [tSvc, setTSvc] = useState('');
  const [tSvcCoef, setTSvcCoef] = useState('1.00');     // coef. numérico de servicios
  const [tForma, setTForma] = useState('1.00');         // coef. forma del lote
  const [tHab, setTHab] = useState('1.00');
  // Otros
  const [desc, setDesc] = useState('');
  const [fotosMain, setFotosMain] = useState([]);
  const [precioAsp, setPrecioAsp] = useState('');
  const [nota, setNota] = useState('');

  // ── Comparables
  const [comp1, setComp1] = useState(initComp());
  const [comp2, setComp2] = useState(initComp());
  const [comp3, setComp3] = useState(initComp());

  // ── Resultado
  const [resultado, setResultado] = useState(null);
  const [manualVRC, setManualVRC] = useState('');
  // Textos editables del reporte
  const [foda, setFoda] = useState({ f: '', o: '', d: '', a: '' });
  const [analisisComp, setAnalisisComp] = useState('');
  const [recomendacion, setRecomendacion] = useState('');
  const [contextoMercado, setContextoMercado] = useState('');
  const [notaFinal, setNotaFinal] = useState('');
  // ── Campos del rediseño v13 (PDF objetivo Liliana/Pilar)
  const [vrcCode, setVrcCode] = useState('');
  const [criterioProfesional, setCriterioProfesional] = useState('');
  const [ctxPrecioZona, setCtxPrecioZona] = useState('');
  const [ctxCompetencia, setCtxCompetencia] = useState('');
  const [ctxAbsorcion, setCtxAbsorcion] = useState('');

  useEffect(() => {
    const query = [dir, zona, 'Mendoza, Argentina'].filter(Boolean).join(', ');
    if (!query || query.length < 6) {
      setMapLocation(null);
      return;
    }

    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${GOOGLE_MAPS_API_KEY}`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();
        const loc = data?.results?.[0]?.geometry?.location;
        if (loc?.lat && loc?.lng) {
          setMapLocation({ lat: loc.lat, lng: loc.lng });
        } else {
          setMapLocation(null);
        }
      } catch (_e) {
        setMapLocation(null);
      }
    }, 350);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [dir, zona]);

  useEffect(() => {
    setComp1(prev => {
      if (prev.antCoefCustom) return prev;
      const nextAntCoef = getAntiguedadCoef(prev.ant);
      return nextAntCoef !== prev.antCoef ? { ...prev, antCoef: nextAntCoef } : prev;
    });
  }, [comp1.ant]);

  useEffect(() => {
    setComp2(prev => {
      if (prev.antCoefCustom) return prev;
      const nextAntCoef = getAntiguedadCoef(prev.ant);
      return nextAntCoef !== prev.antCoef ? { ...prev, antCoef: nextAntCoef } : prev;
    });
  }, [comp2.ant]);

  useEffect(() => {
    setComp3(prev => {
      if (prev.antCoefCustom) return prev;
      const nextAntCoef = getAntiguedadCoef(prev.ant);
      return nextAntCoef !== prev.antCoef ? { ...prev, antCoef: nextAntCoef } : prev;
    });
  }, [comp3.ant]);

  // Pre-caché mapa: Google Static Maps (via proxy) o tiles OSM como fallback
  useEffect(() => {
    if (!mapLocation) { setPdfMapUrl(null); return; }
    let alive = true;
    fetchMapAsBase64(mapLocation.lat, mapLocation.lng)
      .then(base64 => { if (alive && base64) setPdfMapUrl(base64); })
      .catch(() => {});
    return () => { alive = false; };
  }, [mapLocation]);

  // Chart comparativo (canvas 2D nativo): VU por comparable vs VU prom y rango zona.
  useEffect(() => {
    if (!resultado || !chartRef.current) return;
    const cnv = chartRef.current;
    const dpr = window.devicePixelRatio || 1;
    const W = cnv.clientWidth || 520;
    const H = 220;
    cnv.width = W * dpr;
    cnv.height = H * dpr;
    cnv.style.height = `${H}px`;
    const ctx = cnv.getContext('2d');
    if (!ctx) return; // entorno sin canvas (jsdom/tests)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const labels = resultado.vus.map((_, i) => `C${i + 1}`).concat(['Prom.']);
    const values = [...resultado.vus, resultado.vuPromedio];
    const maxV = Math.max(...values) * 1.15;
    const minV = 0;
    const padL = 48, padR = 16, padT = 18, padB = 32;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const barW = chartW / values.length * 0.55;
    const step = chartW / values.length;

    // Ejes
    ctx.strokeStyle = '#e4e5f0';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH * i) / 4;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      const v = maxV - ((maxV - minV) * i) / 4;
      ctx.fillStyle = '#7d7f9e';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(Math.round(v).toString(), padL - 6, y + 3);
    }

    // Barras
    values.forEach((v, i) => {
      const x = padL + step * i + (step - barW) / 2;
      const h = ((v - minV) / (maxV - minV)) * chartH;
      const y = padT + chartH - h;
      const isAvg = i === values.length - 1;
      ctx.fillStyle = isAvg ? '#9B3358' : '#3B3FB5';
      ctx.fillRect(x, y, barW, h);
      ctx.fillStyle = '#1e2040';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('$' + Math.round(v).toLocaleString('en-US'), x + barW / 2, y - 5);
      ctx.fillStyle = '#5a5c7a';
      ctx.font = '11px sans-serif';
      ctx.fillText(labels[i], x + barW / 2, padT + chartH + 16);
    });

    // Línea VU promedio
    const yProm = padT + chartH - ((resultado.vuPromedio - minV) / (maxV - minV)) * chartH;
    ctx.strokeStyle = '#9B3358';
    ctx.setLineDash([4, 4]);
    ctx.beginPath(); ctx.moveTo(padL, yProm); ctx.lineTo(W - padR, yProm); ctx.stroke();
    ctx.setLineDash([]);
  }, [resultado, showResult, pdfMode]);

  // ─── Handlers foto ────────────────────────────────────────────────────────

  const addFotos = useCallback((key, files) => {
    const fileArr = Array.from(files);
    const readers = fileArr.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = e => res(e.target.result);
      r.readAsDataURL(f);
    }));
    Promise.all(readers).then(imgs => {
      if (key === 'main') setFotosMain(prev => [...prev, ...imgs]);
      else if (key === 'c1') setComp1(prev => ({ ...prev, fotos: [...prev.fotos, ...imgs] }));
      else if (key === 'c2') setComp2(prev => ({ ...prev, fotos: [...prev.fotos, ...imgs] }));
      else if (key === 'c3') setComp3(prev => ({ ...prev, fotos: [...prev.fotos, ...imgs] }));
    });
  }, []);

  const removeFoto = (key, idx) => {
    const captureDeleted = (arr) => {
      const item = arr?.[idx];
      if (item && typeof item === 'object' && item.publicId) {
        setFotosEliminadas(prev => prev.includes(item.publicId) ? prev : [...prev, item.publicId]);
      }
    };
    if (key === 'main') { captureDeleted(fotosMain); setFotosMain(prev => prev.filter((_, i) => i !== idx)); }
    else if (key === 'c1') { captureDeleted(comp1.fotos); setComp1(prev => ({ ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) })); }
    else if (key === 'c2') { captureDeleted(comp2.fotos); setComp2(prev => ({ ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) })); }
    else if (key === 'c3') { captureDeleted(comp3.fotos); setComp3(prev => ({ ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) })); }
  };

  // ─── Toggle amenity ───────────────────────────────────────────────────────

  const toggleAmenity = (list, setList, id) => {
    setList(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleCompAmenity = (comp, setComp, id) => {
    setComp(prev => ({
      ...prev,
      amenities: prev.amenities.includes(id)
        ? prev.amenities.filter(x => x !== id)
        : [...prev.amenities, id],
    }));
  };

  const hasComparableData = (comp) => {
    if (!comp) return false;
    const basic = [
      comp.dir,
      comp.precio,
      comp.supCub,
      comp.supSemi,
      comp.supTer,
      comp.ant,
      comp.dorm,
      comp.ban,
      comp.link,
      comp.fechaPub,
      comp.obs,
    ];
    return basic.some(v => String(v || '').trim() !== '')
      || (Array.isArray(comp.amenities) && comp.amenities.length > 0)
      || (Array.isArray(comp.fotos) && comp.fotos.length > 0);
  };

  const hasAnyLoadedData = () => {
    const mainFields = [
      propietario, tipo, dir, zona, supCub, supSemi, supTer,
      antiguedad, dorm, ban, coch, tSup, tFr, tFo,
      svc, tSvc, desc, precioAsp, nota,
    ];

    return mainFields.some(v => String(v || '').trim() !== '')
      || amenitiesProp.length > 0
      || fotosMain.length > 0
      || hasComparableData(comp1)
      || hasComparableData(comp2)
      || (showComp3 && hasComparableData(comp3));
  };

  const handlePreloadDemo = () => {
    if (hasAnyLoadedData()) {
      const ok = window.confirm('Ya hay datos cargados. ¿Querés reemplazarlos por la demo completa?');
      if (!ok) return;
    }

    const agenteDemo = getLoggedUserName(userDetails, user) || 'Martín Atencia';

    setAgente(agenteDemo);
    setPropietario('Juan Carlos Gonzalez');
    setTipoInm('e');
    setTipo('Departamento');
    setDir('Espejo 230, Piso 5°, Ciudad, Mendoza');
    setZona('Residencial');
    setFecha(new Date().toISOString().split('T')[0]);

    setSupCub('120');
    setSupSemi('0');
    setCoefSemi('0.50');
    setSupTer('0');
    setCoefTer('0.50');
    setAntiguedad('4');
    setReciclada(false);
    setDorm('3');
    setBan('2');
    setCoch('1');
    setPiso('5');
    setOri('1.05');
    setEstado('1.00');
    setEstadoCustom('');
    setObsf('1');
    setSvc('Agua, Luz, Gas, Cloacas');
    setFrenteReal('');
    setFrenteTipo('10');
    setCatProp('1.15');
    setAmenitiesProp(['pileta', 'terraza', 'aire_central', 'calefaccion_central']);

    setTSup('');
    setTFr('');
    setTFo('');
    setTTopo('1.00');
    setTSvc('');
    setTSvcCoef('1.00');
    setTForma('1.00');
    setTHab('1.00');

    setDesc('Departamento premium de alto estándar constructivo, terminaciones de primera calidad, edificio con amenities completos, excelente ubicación y luminosidad.');
    setPrecioAsp('380000');
    setNota('Demo premium — escenario de posición ALTA en el mercado. VU promedio esperado ~2300 USD/m².');
    setFotosMain([]);

    setComp1({
      ...initComp(),
      dir: 'Av. San Martín 980, Piso 4°, Ciudad, Mendoza',
      precio: '285000',
      supCub: '118',
      supSemi: '0',
      supTer: '0',
      ant: '3',
      dorm: '3',
      ban: '2',
      estado: '0.97',
      antCoef: '1.00',
      neg: '0.98',
      tiempo: '1.00',
      ubic: '1.00',
      cat: '1.00',
      acceso: '1.00',
      amenities: ['pileta', 'terraza'],
      venta: 'Vendido',
      obs: 'Departamento premium, mismo standard constructivo, vendido hace 2 meses.',
    });

    setComp2({
      ...initComp(),
      dir: 'Necochea 450, Piso 3°, Ciudad, Mendoza',
      precio: '270000',
      supCub: '112',
      supSemi: '0',
      supTer: '0',
      ant: '5',
      dorm: '3',
      ban: '2',
      estado: '0.95',
      antCoef: '0.97',
      neg: '0.96',
      tiempo: '0.98',
      ubic: '1.05',
      cat: '1.00',
      acceso: '1.00',
      amenities: ['pileta', 'aire_central'],
      venta: 'En venta',
      obs: 'Departamento zona céntrica, tasado mejor ubicado que este comparable.',
    });

    setShowComp3(false);
    setComp3(initComp());
    setManualMode(false);
    setManualVRC('');
    setResultado(null);
    setShowResult(false);
    setMapLocation(null);
    setViewMode('agent');
    setStep(1);

    setFoda({
      f: 'Departamento de alto estándar constructivo con terminaciones de primera calidad. Excelente luminosidad y orientación norte-este. Edificio con amenities completos: pileta, terraza, aire acondicionado central y calefacción central. Ubicación estratégica en zona céntrica de alta demanda. Antigüedad de 4 años garantiza bajo costo de mantenimiento.',
      o: 'Zona en proceso de revalorización sostenida en los últimos 3 años. Alta demanda de unidades de 3 dormitorios en el segmento premium. Posibilidad de captación como inversión para renta. Corredor de crecimiento hacia el Este de la ciudad con nuevos desarrollos de infraestructura vial y comercial previstos.',
      d: 'Piso 5° sin ascensor de respaldo en caso de falla técnica. Superficie de terreno nula limita la expansión. Cochera simple en edificio compartido. Antigüedad incipiente pero con garantía constructiva próxima a vencer. Expensas elevadas por amenities que pueden desalentar a compradores con perfil conservador.',
      a: 'Sobreoferta puntual de departamentos de 3 dormitorios en la zona céntrica durante el último trimestre. Posible suba de tasas hipotecarias que enfría la demanda de compradores finales. Ingreso de nuevos proyectos en pozo en la misma manzana con precios de lanzamiento competitivos. Contexto macroeconómico con volatilidad cambiaria que puede postergar decisiones de compra.',
    });
    setAnalisisComp('El inmueble presenta características superiores al promedio del sector. Alto estándar constructivo y ubicación de primer nivel lo posicionan en el segmento premium del mercado. La antigüedad reducida (4 años) minimiza la depreciación por Ross-Heidecke (factor 0,954). El edificio cuenta con amenities completos que generan un diferencial de valor respecto a la competencia directa. La orientación y luminosidad son considerados atributos de primer orden por el mercado objetivo. El VU calculado de USD 2.313/m² resulta consistente con los comparables relevados y superior al piso del rango esperado para la zona residencial céntrica de Mendoza.');
    setRecomendacion('Se recomienda publicar entre USD 364.000 y USD 380.000, priorizando el precio más alto dado el contexto favorable. La propiedad tiene argumentos sólidos para sostener el precio aspiracional del propietario (USD 380.000), dentro del rango del VRC. Estrategia sugerida: exposición digital intensa durante los primeros 30 días con precio tope; si no hay ofertas en ese plazo, ajustar a USD 370.000. Destacar amenities, piso alto y categoría constructiva como diferenciadores clave en la comunicación de venta.');
    setContextoMercado('El mercado residencial céntrico de Mendoza muestra demanda sostenida en el segmento premium de 3 dormitorios. Los plazos de venta promedio para unidades similares oscilan entre 45 y 90 días con precio alineado al VRC. Se registra una leve sobreoferta estacional que presiona los precios hacia el límite inferior del rango. Los comparables relevados presentan baja dispersión (5%), lo que indica un mercado con referencias claras y vendedores informados.');
  };

  // ─── Demo TEST v2 — Casa en Dorrego, Guaymallén, con 2 comparables ───
  //     Ejercita: método comparativo (núcleo), clamp ±15% sobre ajustes
  //     propios, fechaCoef, RH polinómica para antigüedad media.
  const handlePreloadDemoV2 = () => {
    if (hasAnyLoadedData()) {
      const ok = window.confirm('Ya hay datos cargados. ¿Querés reemplazarlos por la demo TEST (Casa Dorrego)?');
      if (!ok) return;
    }

    const agenteDemo = getLoggedUserName(userDetails, user) || 'Martín Atencia';
    const hoy = new Date();
    const fmtDate = d => d.toISOString().split('T')[0];
    const hace30d = new Date(hoy.getTime() - 30 * 86400000);
    const hace60d = new Date(hoy.getTime() - 60 * 86400000);

    setAgente(agenteDemo);
    setPropietario('Cliente Demo');
    setTipoInm('e');
    setTipo('Casa');
    setDir('Tirasso 1234, Dorrego, Guaymallén, Mendoza');
    setZona('Guaymallén');
    setFecha(fmtDate(hoy));

    // Casa típica de Dorrego: 180m² cubierta + 25 semi + 400 terreno.
    setSupCub('180');
    setSupSemi('25');
    setCoefSemi('0.50');
    setSupTer('400');
    setCoefTer('0.50');
    setAntiguedad('12');
    setReciclada(false);
    setDorm('3');
    setBan('2');
    setCoch('1');
    setPiso('');
    setOri('1.00');
    setEstado('0.95');
    setEstadoCustom('');
    setObsf('1.00');
    setSvc('Agua, Luz, Gas, Cloacas');
    setFrenteReal('12');
    setFrenteTipo('10');
    setCatProp('1.10');
    setAmenitiesProp(['quincho', 'jardin', 'garage_techado', 'laundry', 'parrilla']);

    setTSup(''); setTFr(''); setTFo(''); setTTopo('1.00'); setTSvc(''); setTSvcCoef('1.00'); setTForma('1.00'); setTHab('1.00');

    setDesc('Casa familiar en Dorrego, Guaymallén. 3 dormitorios, 2 baños, cochera, quincho con asador, jardín al frente y fondo. Buen estado general, antigüedad media. Frente de 12m sobre calle tranquila.');
    setPrecioAsp('165000');
    setNota('Demo TEST — Casa en Dorrego con 2 comparables. Verifica método comparativo (núcleo) y clamp ±15% sobre ajustes técnicos.');
    setFotosMain([]);

    // Comparable 1 — Casa más vieja (18a) en Dorrego, publicada hace 60 días.
    setComp1({
      ...initComp(),
      dir: 'San Martín Norte 3500, Dorrego, Guaymallén',
      precio: '145000',
      supCub: '160',
      supSemi: '20',
      supTer: '350',
      ant: '18',
      dorm: '3',
      ban: '2',
      estado: '0.92',
      antCoef: '0.90',
      neg: '0.94',
      tiempo: '0.97',
      ubic: '1.00',
      cat: '1.00',
      acceso: '1.00',
      amenities: ['quincho', 'jardin', 'garage_techado', 'laundry'],
      venta: 'En venta',
      fechaPub: fmtDate(hace60d),
      obs: 'Casa más antigua (18a) sin reciclar. Lote similar. Publicada hace 2 meses.',
    });

    // Comparable 2 — Casa más nueva (6a) y mejor categoría, hace 30 días.
    setComp2({
      ...initComp(),
      dir: 'Cnel. Plaza 850, Dorrego, Guaymallén',
      precio: '175000',
      supCub: '195',
      supSemi: '30',
      supTer: '450',
      ant: '6',
      dorm: '3',
      ban: '3',
      estado: '0.97',
      antCoef: '0.97',
      neg: '0.96',
      tiempo: '0.95',
      ubic: '1.00',
      cat: '1.15',
      acceso: '1.02',
      amenities: ['pileta', 'quincho', 'jardin', 'garage_techado', 'laundry', 'seguridad_24h', 'parrilla'],
      venta: 'En venta',
      fechaPub: fmtDate(hace30d),
      obs: 'Casa más nueva (6a) con categoría superior y pileta. Lote algo más grande.',
    });

    // Solo 2 comparables — ocultar C3.
    setShowComp3(false);
    setComp3(initComp());

    setManualMode(false);
    setManualVRC('');
    setResultado(null);
    setShowResult(false);
    setMapLocation(null);
    setViewMode('agent');
    setStep(1);

    setFoda({
      f: 'Casa de 180m² cubierta sobre lote de 400m² en Dorrego. Frente de 12m supera la media zonal (Hofmann-Neill positivo). 3 dormitorios, 2 baños, cochera y quincho con asador. Estado muy bueno, antigüedad media (12 años) con bajo costo de mantenimiento. Categoría constructiva confort.',
      o: 'Dorrego en proceso de consolidación residencial sostenido. Demanda activa de familias jóvenes que buscan casa sobre lote a precio accesible vs. Capital. Conectividad mejorada con accesos al Acceso Este y centro de Guaymallén. Plazos de venta razonables (60-90 días) para producto bien valorado.',
      d: 'Sin pileta (puede ser desventaja frente a comparables que la incluyen). Antigüedad media a considerar en presupuesto de mantenimiento futuro. Una sola cochera limita perfil de compradores con 2 vehículos.',
      a: 'Oferta creciente de casa en zonas vecinas de Guaymallén a precios competitivos. Sensibilidad de la zona al tipo de cambio y tasas de financiación. Posible enfriamiento estacional en otoño-invierno.',
    });
    setAnalisisComp('Casa familiar en Dorrego con superficie homogeneizada de ~392 m². Los 2 comparables relevados se ubican dentro de la misma zona (Dorrego), con dispersión moderada de precio publicado (USD 145.000 vs 175.000). El método comparativo ponderado por similitud de superficie es el núcleo del cálculo. Los coeficientes técnicos (Hofmann-Neill por frente +12m, categoría 1.10, Ross-Heidecke por 12a sin reciclar) afinan el resultado dentro de la banda permitida (±15%). Se recomienda al menos un tercer comparable para mayor robustez estadística.');
    setRecomendacion('Publicar entre USD 155.000 y USD 170.000 según condiciones de mercado al momento del lanzamiento. Plazo estimado 60-90 días. Destacar el frente amplio (12m), el quincho, la cochera y el jardín como diferenciadores. Considerar una sesión fotográfica profesional con drone para resaltar el lote.');
    setContextoMercado('Dorrego, Guaymallén: zona con rango USD 600-1.200 /m² para casas de 3 dormitorios sobre lote propio. Los 2 comparables relevados (uno de 18a y uno de 6a) cubren el espectro de antigüedad típico. La demanda en la zona es estable, con plazos promedio de 60-90 días para producto bien valorado. Recomendamos un tercer comparable para reforzar la robustez del análisis.');
    setCtxPrecioZona('USD 600-1.200 /m²');
    setCtxCompetencia('8-12 publicaciones activas');
    setCtxAbsorcion('60-90 días');
  };

  // ─── Demo TERRENO — Lote en Maipú con 2 comparables ─────────────────────
  //     Ejercita el flujo de terreno: tSup/tFr/tFo/tSvc y comparables
  //     que solo tienen superficie de terreno (supCub=0, supSemi=0).
  const handlePreloadDemoTerreno = () => {
    if (hasAnyLoadedData()) {
      const ok = window.confirm('Ya hay datos cargados. ¿Querés reemplazarlos por la demo TERRENO (Maipú)?');
      if (!ok) return;
    }

    const agenteDemo = getLoggedUserName(userDetails, user) || 'Martín Atencia';
    const hoy = new Date();
    const fmtDate = d => d.toISOString().split('T')[0];
    const hace20d = new Date(hoy.getTime() - 20 * 86400000);
    const hace45d = new Date(hoy.getTime() - 45 * 86400000);

    setAgente(agenteDemo);
    setPropietario('Cliente Demo Terreno');
    setTipoInm('t');
    setTipo('Terreno urbano');
    setDir('Ozamis 4500, Maipú, Mendoza');
    setZona('Maipú');
    setFecha(fmtDate(hoy));

    // Campos edificados a vacío
    setSupCub(''); setSupSemi(''); setCoefSemi('0.50');
    setSupTer(''); setCoefTer('0.50');
    setAntiguedad(''); setReciclada(false);
    setDorm(''); setBan(''); setCoch(''); setPiso('');
    setOri('1.00'); setEstado('1.00'); setEstadoCustom('');
    setObsf('1.00'); setSvc(''); setFrenteReal(''); setFrenteTipo('10');
    setCatProp('1.00'); setAmenitiesProp([]);

    // Campos terreno
    setTSup('800');       // 800 m² de superficie
    setTFr('20');         // 20 m de frente
    setTFo('40');         // 40 m de fondo
    setTTopo('1.00');        // plano
    setTSvc('Agua, Luz, Gas, Cloacas');
    setTSvcCoef('1.00');  // todos los servicios
    setTForma('1.00');    // regular rectangular
    setTHab('1.00');         // residencial urbano

    setDesc('Lote urbano de 800 m² en Maipú, Mendoza. Frente de 20 m sobre calle pavimentada. Todos los servicios disponibles (agua, luz, gas, cloacas). Topografía plana, apto construcción residencial.');
    setPrecioAsp('95000');
    setNota('Demo TERRENO — Lote en Maipú con 2 comparables. Verifica cálculo VRC sin superficie cubierta.');
    setFotosMain([]);

    // Comparable 1 — Lote más chico (600 m²) hace 45 días
    setComp1({
      ...initComp(),
      dir: 'Pueyrredón 2100, Maipú',
      precio: '72000',
      supCub: '0',
      supSemi: '0',
      supTer: '600',
      ant: '',
      dorm: '',
      ban: '',
      estado: '1.00',
      antCoef: '1.00',
      neg: '0.95',
      tiempo: '0.96',
      ubic: '1.00',
      cat: '1.00',
      acceso: '1.00',
      amenities: [],
      venta: 'En venta',
      fechaPub: fmtDate(hace45d),
      obs: 'Lote más chico (600 m²) en zona similar. Frente 15 m. Servicios completos.',
      tFrente: '15', tFondo: '40',
      tTopo: '1.00', tSvcCoef: '1.00', tForma: '1.00', tZonif: '1.00',
    });

    // Comparable 2 — Lote más grande (1000 m²) hace 20 días
    setComp2({
      ...initComp(),
      dir: 'Maza 3300, Maipú',
      precio: '115000',
      supCub: '0',
      supSemi: '0',
      supTer: '1000',
      ant: '',
      dorm: '',
      ban: '',
      estado: '1.00',
      antCoef: '1.00',
      neg: '0.94',
      tiempo: '0.97',
      ubic: '1.02',
      cat: '1.00',
      acceso: '1.00',
      amenities: [],
      venta: 'En venta',
      fechaPub: fmtDate(hace20d),
      obs: 'Lote mayor (1000 m²) con frente 22 m. Excelente ubicación, servicios completos.',
      tFrente: '22', tFondo: '45',
      tTopo: '1.00', tSvcCoef: '1.00', tForma: '1.05', tZonif: '1.00',
    });

    setShowComp3(false);
    setComp3(initComp());

    setManualMode(false);
    setManualVRC('');
    setResultado(null);
    setShowResult(false);
    setMapLocation(null);
    setViewMode('agent');
    setStep(1);

    setFoda({
      f: 'Lote urbano de 800 m² con frente amplio (20 m) sobre calle pavimentada en Maipú. Todos los servicios disponibles. Topografía plana, sin desniveles ni rellenos. Apto para construcción residencial unifamiliar o vivienda multifamiliar de baja densidad.',
      o: 'Maipú en expansión sostenida hacia zona residencial. Demanda activa de lotes para autoconstrucción y desarrollos pequeños. Conectividad con accesos a Capital y zona Este.',
      d: 'Sin construcciones existentes (requiere inversión adicional del comprador). Distancia a centros comerciales y educativos a considerar.',
      a: 'Oferta creciente de lotes en zonas vecinas. Sensibilidad al tipo de cambio para insumos de construcción. Marco regulatorio municipal puede modificar índices edificables.',
    });
    setAnalisisComp('Lote urbano en Maipú con 800 m² de superficie. Los 2 comparables relevados (600 y 1.000 m²) cubren el rango típico de la zona. El método comparativo por superficie ajusta por servicios disponibles y ubicación relativa. Se recomienda incorporar un tercer comparable para reforzar la robustez del análisis.');
    setRecomendacion('Publicar entre USD 88.000 y USD 100.000. Plazo estimado 90-120 días. Destacar el frente amplio, la disponibilidad de servicios completos y la topografía plana como diferenciadores.');
    setContextoMercado('Maipú, Mendoza: zona residencial en expansión con rango USD 90-130 /m² para lotes urbanos con servicios. Demanda estable de compradores particulares y pequeños desarrolladores. Plazos promedio 90-120 días.');
    setCtxPrecioZona('USD 90-130 /m²');
    setCtxCompetencia('15-20 publicaciones activas');
    setCtxAbsorcion('90-120 días');
  };

  // ─── Hidratar estado desde VRC del backend (modo edición) ─────────────────
  useEffect(() => {
    if (!isEditMode || !vrcDetail) return;
    // editId viene de useParams() como string; vrcDetail.id del backend es number.
    // Coercionamos a string para evitar fallo de comparación estricta.
    if (String(vrcDetail.id) !== String(editId)) return;
    const v = vrcDetail;
    const pd = v.propiedadData || {};
    setPropietario(v.propietario || v.propietarioNombre || '');
    setClientId(v.clientId || '');
    // v.agente puede ser objeto {id, nombre, role, email} o string plano
    const agenteNombre = typeof v.agente === 'string' ? v.agente : (v.agente?.nombre || '');
    if (agenteNombre) setAgente(agenteNombre);
    // pd.tipo es 'terreno' | 'edificado'; pd.tipoInmueble es el descriptor (ej. 'Terreno urbano')
    setTipoInm(pd.tipo === 'terreno' ? 't' : 'e');
    setTipo(pd.tipoInmueble || pd.tipo || '');
    setDir(pd.direccion || '');
    setZona(pd.zona || '');
    if (pd.fecha) setFecha(pd.fecha.slice(0, 10));
    setSupCub(pd.supCubierta ?? '');
    setSupSemi(pd.supSemicubierta ?? '');
    setCoefSemi(String(pd.coefSemicubierta ?? '0.50'));
    setSupTer(pd.supTerreno ?? '');
    setCoefTer(String(pd.coefTerreno ?? '0.50'));
    setAntiguedad(pd.antiguedad ?? '');
    setReciclada(!!pd.reciclada);
    setDorm(pd.dormitorios ?? '');
    setBan(pd.banos ?? '');
    setCoch(pd.cocheras ?? '');
    setPiso(pd.piso ?? '');
    setOri(String(pd.orientacion ?? '1'));
    setEstado(String(pd.estado ?? '0.90'));
    setObsf(String(pd.obsf ?? '1'));
    setSvc(pd.servicios || '');
    setFrenteReal(pd.frenteReal ?? '');
    setFrenteTipo(String(pd.frenteTipo ?? '10'));
    setCatProp(String(pd.categoria ?? '1.00'));
    setAmenitiesProp(Array.isArray(pd.amenities) ? pd.amenities : []);
    const t = pd.terreno || {};
    // Helper: los <select> de coeficientes usan options con 2 decimales
    // ("1.00", "0.95"…). El backend persiste numbers (1, 0.95…), por lo que
    // hay que formatear o el select cae al primer option (bug visual: "Esquina"
    // en lugar de "Regular rectangular").
    const fmt2 = (val, def) => {
      const n = parseFloat(val);
      return (Number.isFinite(n) ? n : def).toFixed(2);
    };
    // Helper: campos monetarios persistidos como DECIMAL en backend vuelven
    // como "120000.00" → con <input type="number"> en locale es-AR se ven
    // como "120000,00". Los hidratamos como entero limpio.
    const toIntStr = (val) => {
      if (val === null || val === undefined || val === '') return '';
      const n = parseFloat(val);
      return Number.isFinite(n) ? String(Math.round(n)) : '';
    };
    // El backend también persiste estos coef. en top-level de propiedadData;
    // priorizamos `terreno.*` y caemos a top-level por compat.
    setTSup(t.superficie ?? pd.supTotal ?? '');
    setTFr(t.frente ?? pd.frente ?? '');
    setTFo(t.fondo ?? pd.fondo ?? '');
    setTTopo(fmt2(t.topografia ?? pd.topografia, 1));
    setTSvc(t.servicios || '');
    setTSvcCoef(fmt2(t.serviciosCoef ?? pd.serviciosCoef, 1));
    setTForma(fmt2(t.forma ?? pd.forma, 1));
    setTHab(fmt2(t.habilitacion ?? pd.habilitacion, 1));
    setDesc(pd.descripcion || '');
    setNota(pd.nota || '');
    // Fotos: las existentes vienen en fotosInmueble[] top-level
    setFotosMain(Array.isArray(v.fotosInmueble) ? v.fotosInmueble.map(f => f.url || f) : []);
    // Comparables
    const hydrateComp = (cBack, cFotos) => {
      if (!cBack) return initComp();
      return {
        ...initComp(),
        dir: cBack.direccion || '',
        precio: toIntStr(cBack.precioPublicacion),
        supCub: cBack.supCubierta ?? '',
        supSemi: cBack.supSemicubierta ?? '',
        supTer: cBack.supTerreno ?? '',
        ant: cBack.antiguedad ?? '',
        dorm: cBack.dormitorios ?? '',
        ban: cBack.banos ?? '',
        estado: String(cBack.estado ?? '0.90'),
        antCoef: String(cBack.antCoef ?? '0.70'),
        neg: String(cBack.negociacion ?? '0.94'),
        tiempo: String(cBack.tiempo ?? '1.00'),
        ubic: String(cBack.ubicacion ?? '1.00'),
        cat: String(cBack.categoria ?? '1.00'),
        acceso: String(cBack.acceso ?? '1.00'),
        amenities: Array.isArray(cBack.amenities) ? cBack.amenities : [],
        // Coef. terreno por comparable (se perdían al persistir antes de este fix).
        tTopo: String(cBack.tTopo ?? '1.00'),
        tForma: String(cBack.tForma ?? '1.00'),
        tSvcCoef: String(cBack.tSvcCoef ?? '1.00'),
        tHab: String(cBack.tHab ?? '1.00'),
        tSvc: cBack.tSvc || '',
        link: cBack.link || '',
        venta: cBack.venta || 'En venta',
        fechaPub: cBack.fechaPub ? String(cBack.fechaPub).slice(0, 10) : '',
        obs: cBack.obs || '',
        fotos: Array.isArray(cFotos) ? cFotos.map(f => f.url || f) : [],
      };
    };
    const comps = Array.isArray(v.comparables) ? v.comparables : [];
    const fc = v.fotosComparables || {};
    setComp1(hydrateComp(comps[0], fc.c1));
    setComp2(hydrateComp(comps[1], fc.c2));
    if (comps[2]) {
      setShowComp3(true);
      setComp3(hydrateComp(comps[2], fc.c3));
    }
    // Resultado y textos
    setPrecioAsp(toIntStr(v.precioAspiracional));
    if (v.manualVRC) {
      setManualMode(true);
      setManualVRC(toIntStr(v.manualVRC));
    }
    if (v.foda) setFoda({ f: v.foda.f || '', o: v.foda.o || '', d: v.foda.d || '', a: v.foda.a || '' });
    const an = v.analisis || {};
    setAnalisisComp(an.comparativo || '');
    setRecomendacion(an.recomendacion || '');
    setContextoMercado(an.contextoMercado || '');
    setNotaFinal(an.notaFinal || '');
    setCriterioProfesional(an.criterioProfesional || '');
    setCtxPrecioZona(an.ctxPrecioZona || '');
    setCtxCompetencia(an.ctxCompetencia || '');
    setCtxAbsorcion(an.ctxAbsorcion || '');
    if (v.vrcCode) setVrcCode(v.vrcCode);
    if (v.resultados) {
      const storedResult = normalizeStoredResultado(v.resultados);
      setResultado(storedResult);
      setShowResult(storedResult.vrc > 0);
    }
    setStep(5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vrcDetail, isEditMode, editId]);

  // ─── Calcular VRC ─────────────────────────────────────────────────────────

  const handleCalc = () => {
    setCalcError(null);
    setCalcWarnings([]);
    setCalcErrorsList([]);
    setFieldErrors({});

    const esTerreno = tipoInm === 't';
    const manualEnabled = manualMode && manualVRC && parseFloat(manualVRC) > 0;

    // ── Validación estructurada (mismo patrón que CreateProperty) ──────────
    const { isValid, errors, errorsMap, warnings } = validateVRCInputs({
      tipoInm, manualMode, manualVRC, showComp3,
      comp1, comp2, comp3,
      supCub, supSemi, supTer,
      tSup, tFr, tFo,
      precioAsp,
      antiguedad, dorm, ban, coch, piso,
      frenteReal,
      zona, dir, propietario,
    });

    if (!isValid) {
      setFieldErrors(errorsMap);
      setCalcErrorsList(errors);
      // Mensaje resumen compacto para el alert principal
      setCalcError(errors.map(e => e.message).join(' · '));
      setCalcWarnings(warnings);
      return;
    }
    if (warnings.length > 0) setCalcWarnings(warnings);

    // ── Armar payload normalizado (terreno usa tSup como supTer) ───────────
    const comparables = [comp1, comp2, ...(showComp3 ? [comp3] : [])];
    const payloadSup = esTerreno
      ? { supCub: 0, supSemi: 0, coefSemi: 0.5, supTer: tSup, coefTer: 1 }
      : { supCub, supSemi, coefSemi, supTer, coefTer };

    setLoading(true);
    setTimeout(() => {
      try {
        const res = calcVRC({
          tipo: tipoInm,
          ...payloadSup,
          antiguedad: esTerreno ? 0 : antiguedad,
          reciclada: esTerreno ? false : reciclada,
          ori, catProp, obsf,
          estado: esTerreno ? 1 : (estadoCustom || estado),
          frenteReal: esTerreno ? (tFr || frenteReal) : frenteReal,
          frenteTipo,
          piso: esTerreno ? 0 : piso,
          amenitiesProp: esTerreno ? [] : amenitiesProp,
          zona,
          comparables,
          manualVRC: manualEnabled ? parseFloat(manualVRC) : null,
          esTerreno,
          tTopoProp: tTopo,
          tSvcCoefProp: tSvcCoef,
          tFormaProp: tForma,
          tHabProp: tHab,
        });
        if (!res) {
          setCalcError('No se pudo calcular el VRC con los datos cargados. Revisá superficie del inmueble y los comparables.');
          setLoading(false);
          return;
        }
        setResultado(res);
        // El backend es la fuente de verdad del vrcCode (lo asigna atómicamente al guardar).
        // No generamos código local para evitar inconsistencias visuales.
        setLoading(false);
        setShowResult(true);
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      } catch (e) {
        console.error('[VRCPro] error en cálculo:', e);
        setCalcError('Error inesperado al calcular: ' + (e?.message || 'fallo interno'));
        setLoading(false);
      }
    }, 600);
  };

  // ─── Guardar VRC en backend ───────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!resultado) {
      setSaveError('Primero calculá el VRC antes de guardar.');
      return;
    }
    // Re-validar antes de guardar (por si editaron campos después del cálculo)
    const { isValid, errors, errorsMap } = validateVRCInputs({
      tipoInm, manualMode, manualVRC, showComp3,
      comp1, comp2, comp3,
      supCub, supSemi, supTer,
      tSup, tFr, tFo,
      precioAsp,
      antiguedad, dorm, ban, coch, piso,
      frenteReal,
      zona, dir, propietario,
    });
    if (!isValid) {
      setFieldErrors(errorsMap);
      setCalcErrorsList(errors);
      setSaveError('Hay errores en el formulario que deben corregirse antes de guardar.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const stateSnapshot = {
        clientId: clientId || null,
        propietario, agente,
        tipoInm, tipo, dir, zona, fecha,
        supCub, supSemi, coefSemi, supTer, coefTer,
        antiguedad, reciclada, dorm, ban, coch, piso, ori,
        estado, estadoCustom, obsf, svc,
        frenteReal, frenteTipo, catProp, amenitiesProp,
        tSup, tFr, tFo, tTopo, tSvc, tSvcCoef, tForma, tHab,
        desc, precioAsp, nota,
        fotosMain,
        comp1, comp2, comp3: showComp3 ? comp3 : null,
        resultado,
        manualMode, manualVRC,
        foda, analisisComp, recomendacion, contextoMercado, notaFinal,
        criterioProfesional, ctxPrecioZona, ctxCompetencia, ctxAbsorcion,
        fotosEliminadas,
      };
      const payload = await buildVRCPayload(stateSnapshot);
      // Backend espera sellerId en el body (auth pragmática). Lo tomamos del
      // store redux (mismo patrón usado en CreateProperty y la SideBar) y, como
      // fallback, de localStorage / user.user.id.
      const resolvedSellerId =
        sellerIdFromStore
        || user?.userId
        || user?.user?.id
        || (typeof window !== 'undefined' ? localStorage.getItem('userId') : null);
      const resolvedRole = (
        (typeof window !== 'undefined' ? localStorage.getItem('userType') : '')
        || user?.userType
        || ''
      ).toLowerCase();
      let extra = { sellerId: resolvedSellerId, role: resolvedRole || undefined };
      if (isEditMode) {
        const reason = window.prompt('Motivo del cambio (opcional):', '') || '';
        extra = { ...extra, changeReason: reason };
      }
      const fd = buildVRCFormData(payload, extra);
      // El backend lee req.body.sellerId / req.body.role como campos planos del
      // multipart (no dentro del JSON 'data'). Los appendeamos a top-level.
      if (resolvedSellerId) fd.append('sellerId', String(resolvedSellerId));
      if (resolvedRole) fd.append('role', resolvedRole);
      const res = isEditMode
        ? await dispatch(updateVRCAction(editId, fd))
        : await dispatch(createVRCAction(fd));
      const id = res?.data?.id || res?.id || editId;
      setSavedId(id);
      if (id) {
        // Persistimos vrcCode oficial si llegó del backend
        if (res?.data?.vrcCode) setVrcCode(res.data.vrcCode);
        // Navegamos al wizard en modo edición con los datos ya guardados
        // (mismo patrón que EditPropertyForm: form unificado para crear/ver/editar)
        setTimeout(() => navigate(`/vrc-editar/${id}`), 300);
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      const fieldErrs = err?.response?.data?.errors;
      const detail = Array.isArray(fieldErrs)
        ? fieldErrs.map(e => `${e.field}: ${e.message}`).join(' · ')
        : '';
      setSaveError([apiMsg || err.message || 'Error al guardar', detail].filter(Boolean).join(' — '));
    } finally {
      setSaving(false);
    }
  }, [
    resultado, isEditMode, editId, dispatch, navigate,
    clientId, propietario, agente, tipoInm, tipo, dir, zona, fecha,
    supCub, supSemi, coefSemi, supTer, coefTer,
    antiguedad, reciclada, dorm, ban, coch, piso, ori,
    estado, estadoCustom, obsf, svc,
    frenteReal, frenteTipo, catProp, amenitiesProp,
    tSup, tFr, tFo, tTopo, tSvc, tHab,
    desc, precioAsp, nota, fotosMain,
    comp1, comp2, comp3, showComp3,
    manualMode, manualVRC,
    foda, analisisComp, recomendacion, contextoMercado, notaFinal,
    criterioProfesional, ctxPrecioZona, ctxCompetencia, ctxAbsorcion,
    fotosEliminadas,
  ]);

  // ─── Generar PDF ──────────────────────────────────────────────────────────
  // mode: 'agent' → informe técnico completo (portada técnica + páginas 2-6).
  //       'prop'  → informe limpio de 4 páginas para el propietario.

  const generatePDF = async (mode = 'agent') => {
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;

    const prevViewMode = viewMode;

    // 1. Asegurar que el mapa estático esté listo ANTES de entrar en pdfMode
    //    (si no, la primera generación queda con fallback de texto).
    let mapUrl = pdfMapUrl;
    if (!mapUrl && mapLocation) {
      try {
        mapUrl = await fetchMapAsBase64(mapLocation.lat, mapLocation.lng);
        if (mapUrl) setPdfMapUrl(mapUrl);
      } catch { /* seguimos sin mapa */ }
    }

    setPdfMode(true);
    setViewMode(mode);
    // Esperar a que React aplique pdf-mode y la <img> del mapa cargue
    await new Promise(r => setTimeout(r, 450));

    // Precargar TODAS las imágenes (base64 y remotas) dentro del resultado antes
    // de capturar con html2canvas. Sin esto, las fotos base64 grandes pueden
    // capturarse vacías porque html2canvas no espera a decodificar dataURLs largos.
    if (resultRef.current) {
      const imgs = Array.from(resultRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise(res => {
          const done = () => res();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          setTimeout(done, 3000); // failsafe por imagen
        });
      }));
      // Pequeño settle adicional para layout post-decode
      await new Promise(r => setTimeout(r, 150));
    }

    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210;
      const H = 297;
      let pageHasContent = false; // ¿la página actual ya tiene contenido?

      // Helper: captura un ref y lo agrega al PDF con saltos de página inteligentes.
      // Inicia en página nueva SOLO si la página actual ya tiene contenido.
      // Si el ref contiene elementos con `data-pdf-break`, sus posiciones Y
      // se usan como puntos de corte preferidos sobre el slicer por brillo.
      const addSection = async (ref, bg = '#f4f5fb') => {
        if (!ref.current) return;
        // Calcular posiciones de breakpoints duros relativos al ref ANTES del capture
        const refRect = ref.current.getBoundingClientRect();
        const breakYsCss = Array.from(ref.current.querySelectorAll('[data-pdf-break]'))
          .map(el => el.getBoundingClientRect().top - refRect.top)
          .filter(y => y > 20); // ignorar el que cae al inicio
        const canvas = await html2canvas(ref.current, {
          scale: 2, useCORS: true, allowTaint: true,
          backgroundColor: bg, logging: false, imageTimeout: 15000,
        });
        const scaleY = canvas.height / ref.current.offsetHeight;
        const breakPxAll = breakYsCss.map(y => Math.round(y * scaleY));
        const ctx = canvas.getContext('2d');

        // Verificar que el canvas tiene contenido visible
        let canvasHasContent = false;
        for (let cy = 0; cy < Math.min(canvas.height, 300); cy += 4) {
          const row = ctx.getImageData(0, cy, canvas.width, 2);
          let sum = 0;
          for (let k = 0; k < row.data.length; k += 4) sum += (row.data[k] + row.data[k + 1] + row.data[k + 2]) / 3;
          if (sum / (canvas.width * 2 * 255) < 0.97) { canvasHasContent = true; break; }
        }
        if (!canvasHasContent) return;

        const pxPerMm = canvas.width / W;
        const pageHeightPx = Math.round(H * pxPerMm);
        let startPx = 0;

        // Nueva página SOLO si la actual ya tiene algo (evita blancos)
        if (pageHasContent) pdf.addPage();
        pageHasContent = true;

        while (startPx < canvas.height) {
          const remainingPx = canvas.height - startPx;
          let breakPx = startPx + Math.min(pageHeightPx, remainingPx);
          if (remainingPx > pageHeightPx * 1.05) {
            // 1) Preferir breakpoints duros (data-pdf-break) que caigan dentro
            //    de la franja 45%-100% de la página actual. Bajamos el umbral
            //    para evitar cortar tablas/galerías de fotos a la mitad.
            const hardMin = startPx + Math.round(pageHeightPx * 0.45);
            const hardMax = startPx + pageHeightPx;
            const hardCandidates = breakPxAll.filter(y => y >= hardMin && y <= hardMax);
            if (hardCandidates.length > 0) {
              // Usar el último candidato dentro de la franja (maximiza contenido por página)
              breakPx = Math.max(...hardCandidates);
            } else {
              // 2) Fallback: slicer por brillo (84%-99%)
              const scanStart = startPx + Math.round(pageHeightPx * 0.84);
              const scanEnd   = startPx + Math.round(pageHeightPx * 0.99);
              let bestBrightness = -1;
              for (let scanY = scanStart; scanY <= Math.min(scanEnd, canvas.height - 4); scanY += 4) {
                const row = ctx.getImageData(0, scanY, canvas.width, 4);
                let sum = 0;
                for (let k = 0; k < row.data.length; k += 4) sum += (row.data[k] + row.data[k + 1] + row.data[k + 2]) / 3;
                const brightness = sum / (canvas.width * 4 * 255);
                if (brightness > bestBrightness) { bestBrightness = brightness; breakPx = scanY; }
              }
            }
          }
          // Extraer un sub-canvas exacto [startPx, breakPx] para esta página.
          // Esto evita que html2canvas + addImage con offset negativo "filtre"
          // contenido más allá del límite de página (causa real de la
          // duplicación visual de tablas/fotos entre páginas consecutivas).
          const sliceHpx = Math.max(1, breakPx - startPx);
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHpx;
          const sctx = sliceCanvas.getContext('2d');
          sctx.fillStyle = bg;
          sctx.fillRect(0, 0, canvas.width, sliceHpx);
          sctx.drawImage(canvas, 0, -startPx);
          const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
          const sliceMm = (sliceHpx / canvas.width) * W;
          pdf.addImage(sliceData, 'JPEG', 0, 0, W, sliceMm);

          startPx = breakPx;
          if (startPx < canvas.height) {
            let hasContent = false;
            for (let checkY = startPx; checkY < canvas.height; checkY += 6) {
              const row = ctx.getImageData(0, checkY, canvas.width, 2);
              let sum = 0;
              for (let k = 0; k < row.data.length; k += 4) sum += (row.data[k] + row.data[k + 1] + row.data[k + 2]) / 3;
              if (sum / (canvas.width * 2 * 255) < 0.97) { hasContent = true; break; }
            }
            if (hasContent) { pdf.addPage(); pageHasContent = true; }
            else break;
          }
        }
      };

      if (mode === 'prop') {
        // Informe propietario: 4 páginas limpias (la portada es la página 1).
        await addSection(propPage1Ref, '#fff');
        await addSection(propPage2Ref, '#fff');
        await addSection(propPage3Ref, '#fff');
        await addSection(propPage4Ref, '#fff');
        pdf.save(`VRC_${tipo || 'Inmueble'}_${dir || 'BY_Raices'}.pdf`);
      } else {
        // PORTADA técnica (página 1) — se fuerza a A4 completo para evitar página corta
        if (coverRef.current) {
          coverRef.current.style.display = 'flex';
          const coverCanvas = await html2canvas(coverRef.current, {
            scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#fff',
          });
          coverRef.current.style.display = 'none';
          const coverData = coverCanvas.toDataURL('image/jpeg', 0.92);
          // El CSS de .pdfCover es 794x1123 (ratio A4 a 96dpi) → estirado a 210x297 sin distorsión visible
          pdf.addImage(coverData, 'JPEG', 0, 0, W, H);
          pageHasContent = true;
        }

        // Páginas siguientes — addPage solo si la actual tiene contenido
        await addSection(page2Ref);
        await addSection(page3Ref);
        await addSection(page4Ref);
        await addSection(page5Ref);
        await addSection(page6Ref, '#fff');

        pdf.save(`VRC_tecnico_${tipo || 'Inmueble'}_${dir || 'BY_Raices'}.pdf`);
      }
    } finally {
      setPdfMode(false);
      setViewMode(prevViewMode);
    }
  };

  // ─── Semáforo ─────────────────────────────────────────────────────────────

  const getSemaforo = () => {
    if (!resultado || !precioAsp) return null;
    const asp = parseFloat(precioAsp);
    const vrc = resultado.vrc;
    const ratio = asp / vrc;
    if (ratio <= 1.05) return { color: 'green', label: 'Alineado', sub: 'Precio aspiracional cerca del VRC' };
    if (ratio <= 1.20) return { color: 'amber', label: 'Sobrevalorado', sub: `+${((ratio - 1) * 100).toFixed(0)}% sobre el VRC` };
    return { color: 'red', label: 'Muy sobrevalorado', sub: `+${((ratio - 1) * 100).toFixed(0)}% sobre el VRC · difícil de vender` };
  };

  const semaforo = getSemaforo();

  // ─── Render helpers ───────────────────────────────────────────────────────

  const renderFotos = (fotos, key) => {
    if (!fotos.length) return null;
    return (
      <div className="thumbs">
        {fotos.map((src, i) => (
          <div key={i} className={`thumb ${i === 0 ? 'lg' : 'sm'}`}>
            <img src={src} alt="" />
            <button className="rm" onClick={() => removeFoto(key, i)}>✕</button>
          </div>
        ))}
      </div>
    );
  };

  const renderCompForm = (comp, setComp, label, color, idx) => {
    const esTerrenoComp = tipoInm === 't';
    return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: '1.1rem' }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: color === 'wine' ? 'var(--wine)' : color === 'green' ? 'var(--green)' : 'var(--blue)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>C{idx}</div>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>Comparable {idx}{esTerrenoComp ? ' · Terreno / Lote' : ''}</div>
          <div style={{ fontSize: 10.5, color: 'var(--mid)' }}>{label}</div>
        </div>
      </div>
      <div className="g2">
        <div className="f"><label>Dirección</label><input type="text" value={comp.dir} onChange={e => setComp(p => ({ ...p, dir: e.target.value }))} placeholder={`Ej: Calle 123`} /></div>
        <div className="f"><label>Precio publicado (USD)</label><div className="ui"><input type="number" min="0" value={comp.precio} onChange={e => setComp(p => ({ ...p, precio: e.target.value }))} placeholder="0" /><span className="un">USD</span></div></div>
        {!esTerrenoComp && (
          <>
            <div className="f"><label>Sup. cubierta (m²)</label><div className="ui"><input type="number" min="0" value={comp.supCub} onChange={e => setComp(p => ({ ...p, supCub: e.target.value }))} placeholder="0" /><span className="un">m²</span></div></div>
            <div className="f"><label>Sup. semicubierta (m²)</label><div className="ui"><input type="number" min="0" value={comp.supSemi} onChange={e => setComp(p => ({ ...p, supSemi: e.target.value }))} placeholder="0" /><span className="un">m²</span></div></div>
          </>
        )}
        <div className="f"><label>Sup. terreno (m²){esTerrenoComp ? ' · principal' : ''}</label><div className="ui"><input type="number" min="0" value={comp.supTer} onChange={e => setComp(p => ({ ...p, supTer: e.target.value }))} placeholder="0" /><span className="un">m²</span></div></div>
        {esTerrenoComp && (
          <>
            <div className="f"><label>Frente (m)</label><div className="ui"><input type="number" min="0" value={comp.tFrente} onChange={e => setComp(p => ({ ...p, tFrente: e.target.value }))} placeholder="0" /><span className="un">m</span></div></div>
            <div className="f"><label>Fondo (m)</label><div className="ui"><input type="number" min="0" value={comp.tFondo} onChange={e => setComp(p => ({ ...p, tFondo: e.target.value }))} placeholder="0" /><span className="un">m</span></div></div>
          </>
        )}
        {!esTerrenoComp && (
          <>
            <div className="f"><label>Antigüedad (años)</label><input type="number" min="0" value={comp.ant} onChange={e => setComp(p => ({ ...p, ant: e.target.value }))} placeholder="0" /></div>
            <div className="f"><label>Dormitorios</label><input type="number" min="0" value={comp.dorm} onChange={e => setComp(p => ({ ...p, dorm: e.target.value }))} placeholder="0" /></div>
            <div className="f"><label>Baños</label><input type="number" min="0" value={comp.ban} onChange={e => setComp(p => ({ ...p, ban: e.target.value }))} placeholder="0" /></div>
          </>
        )}
      </div>

      <div className="cbox">
        <div className="cbox-t">Coeficientes de ajuste</div>
        <div className="g4">
          {!esTerrenoComp && (
          <div className="f">
            <label>Estado</label>
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <select style={{ flex: 1 }} value={resolveSelectValue(comp.estado, comp.estadoCustom, ESTADO_OPTIONS)} onChange={e => {
                const val = e.target.value;
                setComp(p => ({ ...p, estado: val === 'custom' ? p.estadoCustom || p.estado : val, estadoCustom: val === 'custom' ? p.estadoCustom : '' }));
              }}>
                <option value="1.00">Excelente (1.00)</option>
                <option value="0.97">Muy buena+ (0.97)</option>
                <option value="0.95">Muy buena (0.95)</option>
                <option value="0.92">Buena+ (0.92)</option>
                <option value="0.90">Buena (0.90)</option>
                <option value="0.87">Buena- (0.87)</option>
                <option value="0.85">Regular+ (0.85)</option>
                <option value="0.80">Regular (0.80)</option>
                <option value="0.70">Mala (0.70)</option>
                <option value="0.50">Pésima (0.50)</option>
                <option value="custom">Personalizado...</option>
              </select>
              <input type="number" min="0.1" max="1.5" step="0.01" placeholder="0.90" style={{ width: 64, flexShrink: 0 }} value={comp.estadoCustom} onChange={e => setComp(p => ({ ...p, estadoCustom: e.target.value, estado: e.target.value || p.estado }))} />
            </div>
            <div className="hint">Coef. real aplicado: {fmtN(comp.estado || 0.9, 2)}</div>
          </div>
          )}
          {!esTerrenoComp && (
          <div className="f">
            <label>Antigüedad</label>
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <select style={{ flex: 1 }} value={resolveSelectValue(comp.antCoef, comp.antCoefCustom, ANT_OPTIONS)} onChange={e => {
                const val = e.target.value;
                setComp(p => ({ ...p, antCoef: val === 'custom' ? p.antCoefCustom || p.antCoef : val, antCoefCustom: val === 'custom' ? p.antCoefCustom : '' }));
              }}>
                <option value="1.00">0–5 años (1.00)</option>
                <option value="0.97">5–8 años (0.97)</option>
                <option value="0.95">8–10 años (0.95)</option>
                <option value="0.92">10–15 años (0.92)</option>
                <option value="0.90">15–20 años (0.90)</option>
                <option value="0.87">20–25 años (0.87)</option>
                <option value="0.85">25–30 años (0.85)</option>
                <option value="0.78">30–40 años (0.78)</option>
                <option value="0.70">40+ años (0.70)</option>
                <option value="custom">Personalizado...</option>
              </select>
              <input type="number" min="0.1" max="1.5" step="0.01" placeholder="0.70" style={{ width: 64, flexShrink: 0 }} value={comp.antCoefCustom} onChange={e => setComp(p => ({ ...p, antCoefCustom: e.target.value, antCoef: e.target.value || p.antCoef }))} />
            </div>
            <div className="hint">Años: {comp.ant || '0'} · Coef. real aplicado: {fmtN(comp.antCoef || 0.7, 2)}</div>
          </div>
          )}
          <div className="f">
            <label>Negociabilidad</label>
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <select style={{ flex: 1 }} value={resolveSelectValue(comp.neg, comp.negCustom, NEG_OPTIONS)} onChange={e => {
                const val = e.target.value;
                setComp(p => ({ ...p, neg: val === 'custom' ? p.negCustom || p.neg : val, negCustom: val === 'custom' ? p.negCustom : '' }));
              }}>
                <option value="1.00">Firme (1.00)</option>
                <option value="0.98">Poco neg. (0.98)</option>
                <option value="0.96">Algo neg. (0.96)</option>
                <option value="0.94">Negociable (0.94)</option>
                <option value="0.92">Bastante neg. (0.92)</option>
                <option value="0.90">Muy neg. (0.90)</option>
                <option value="0.85">Urgente venta (0.85)</option>
                <option value="custom">Personalizado...</option>
              </select>
              <input type="number" min="0.1" max="1.1" step="0.01" placeholder="0.94" style={{ width: 64, flexShrink: 0 }} value={comp.negCustom} onChange={e => setComp(p => ({ ...p, negCustom: e.target.value, neg: e.target.value || p.neg }))} />
            </div>
            <div className="hint">Coef. real aplicado: {fmtN(comp.neg || 0.94, 2)}</div>
          </div>
          <div className="f">
            <label>Tiempo mercado</label>
            <select value={comp.tiempo} onChange={e => setComp(p => ({ ...p, tiempo: e.target.value }))}>
              <option value="1.00">-1 mes</option>
              <option value="0.98">1–3 meses</option>
              <option value="0.95">3–6 meses</option>
              <option value="0.90">+6 meses</option>
            </select>
          </div>
        </div>
        <div className="f" style={{ marginTop: '.625rem' }}>
          <label>Ubicación: el inmueble TASADO vs este comparable</label>
          <select value={comp.ubic} onChange={e => setComp(p => ({ ...p, ubic: e.target.value }))}>
            <option value="1.00">Similar ubicación (1.00)</option>
            <option value="1.05">Tasado MEJOR ubicado (+5%)</option>
            <option value="1.10">Tasado MUCHO MEJOR ubicado (+10%)</option>
            <option value="0.95">Tasado PEOR ubicado (-5%)</option>
            <option value="0.90">Tasado MUCHO PEOR ubicado (-10%)</option>
          </select>
          <div className="hint">¿El inmueble que estás tasando está mejor o peor ubicado que este comparable?</div>
        </div>
        {!esTerrenoComp && (
        <div className="f" style={{ marginTop: '.625rem' }}>
          <label>Categoría constructiva</label>
          <select value={comp.cat} onChange={e => setComp(p => ({ ...p, cat: e.target.value }))}>
            <option value="0.85">Economía (0.85)</option>
            <option value="1.00">Estándar (1.00)</option>
            <option value="1.15">Confort (1.15)</option>
            <option value="1.30">Lujo (1.30)</option>
            <option value="1.50">Premium (1.50)</option>
          </select>
        </div>
        )}
        {esTerrenoComp && (
          <div className="g2" style={{ marginTop: '.625rem' }}>
            <div className="f"><label>Topografía del comparable</label>
              <select value={comp.tTopo} onChange={e => setComp(p => ({ ...p, tTopo: e.target.value }))}>
                <option value="1.00">Plano (1.00)</option>
                <option value="0.95">Leve pendiente (0.95)</option>
                <option value="0.85">Pronunciada (0.85)</option>
                <option value="0.70">Irregular (0.70)</option>
              </select>
            </div>
            <div className="f"><label>Servicios del comparable</label>
              <select value={comp.tSvcCoef} onChange={e => setComp(p => ({ ...p, tSvcCoef: e.target.value }))}>
                <option value="1.00">Todos (1.00)</option>
                <option value="0.97">Sin gas (0.97)</option>
                <option value="0.94">Sin gas ni cloacas (0.94)</option>
                <option value="0.88">Solo luz / factibles (0.88)</option>
                <option value="0.82">Sin servicios (0.82)</option>
              </select>
            </div>
            <div className="f"><label>Forma del lote</label>
              <select value={comp.tForma} onChange={e => setComp(p => ({ ...p, tForma: e.target.value }))}>
                <option value="1.05">Esquina (1.05)</option>
                <option value="1.00">Regular rectangular (1.00)</option>
                <option value="0.97">Trapezoidal leve (0.97)</option>
                <option value="0.93">Irregular (0.93)</option>
                <option value="0.88">Muy irregular / interior (0.88)</option>
              </select>
            </div>
            <div className="f"><label>Zonificación / Habilitación</label>
              <select value={comp.tZonif} onChange={e => setComp(p => ({ ...p, tZonif: e.target.value }))}>
                <option value="1.08">Comercial premium (1.08)</option>
                <option value="1.03">Mixto (1.03)</option>
                <option value="1.00">Residencial urbano (1.00)</option>
                <option value="0.90">Urbanizable (0.90)</option>
                <option value="0.70">Rural (0.70)</option>
              </select>
            </div>
          </div>
        )}
        <div className="f" style={{ marginTop: '.625rem' }}>
          <label>Acceso al inmueble</label>
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <select style={{ flex: 1 }} value={resolveSelectValue(comp.acceso, comp.accesoCustom, ACCESO_OPTIONS)} onChange={e => {
              const val = e.target.value;
              setComp(p => ({ ...p, acceso: val === 'custom' ? p.accesoCustom || p.acceso : val, accesoCustom: val === 'custom' ? p.accesoCustom : '' }));
            }}>
              <option value="1.05">Acceso privado premium (+5%)</option>
              <option value="1.02">Asfaltada privada/cerrada (+2%)</option>
              <option value="1.00">Calle asfaltada (1.00)</option>
              <option value="0.98">Asfaltada c/ tránsito</option>
              <option value="0.96">Calle de tierra (-4%)</option>
              <option value="0.94">Tierra mejorada (-6%)</option>
              <option value="0.92">Tierra sin servicios (-8%)</option>
              <option value="0.88">Acceso difícil (-12%)</option>
              <option value="custom">Personalizado...</option>
            </select>
            <input type="number" min="0.5" max="1.3" step="0.01" placeholder="1.00" style={{ width: 64, flexShrink: 0 }} value={comp.accesoCustom} onChange={e => setComp(p => ({ ...p, accesoCustom: e.target.value, acceso: e.target.value || p.acceso }))} />
          </div>
          <div className="hint">Coef. real aplicado: {fmtN(comp.acceso || 1, 2)}</div>
        </div>
      </div>

      {!esTerrenoComp && (
      <>
      <div className="sep" style={{ margin: '.875rem 0' }} />
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: '.5rem' }}>Amenities del Comparable {idx}</div>
      <div className="am-grid">
        {AMENITIES_COMP.map(a => (
          <label key={a.id} className={`am-c${comp.amenities.includes(a.id) ? ' on' : ''}`} onClick={() => toggleCompAmenity(comp, setComp, a.id)}>
            {a.label}
          </label>
        ))}
      </div>
      </>
      )}

      <div className="g2" style={{ marginTop: '.75rem' }}>
        <div className="f"><label>Link publicación</label><input type="url" value={comp.link} onChange={e => setComp(p => ({ ...p, link: e.target.value }))} placeholder="https://zonaprop..." /></div>
        <div className="f">
          <label>Estado venta</label>
          <select value={comp.venta} onChange={e => {
            const vt = e.target.value;
            // autoNeg: Vendido=1.00 · En venta=0.94 · En negociación=0.96
            const autoVal = vt === 'Vendido' ? '1.00' : vt === 'En negociación' ? '0.96' : '0.94';
            setComp(p => ({ ...p, venta: vt, neg: autoVal, negCustom: '' }));
          }}>
            <option>En venta</option>
            <option>Vendido</option>
            <option>En negociación</option>
          </select>
          <div className="hint">Auto-ajusta negociabilidad</div>
        </div>
        <div className="f"><label>Fecha publicación</label><input type="date" value={comp.fechaPub} onChange={e => setComp(p => ({ ...p, fechaPub: e.target.value }))} /><div className="hint">Para ajuste por tiempo transcurrido</div></div>
      </div>
      <div className="f" style={{ marginTop: '.625rem' }}>
        <label>Observaciones</label>
        <textarea rows={2} value={comp.obs} onChange={e => setComp(p => ({ ...p, obs: e.target.value }))} placeholder="Estado, características, comparación..." />
      </div>
      <div className="sep" />
      <div className="ct" style={{ marginBottom: '.625rem' }}>Fotos Comparable {idx}</div>
      <div className="pzone" style={{ padding: '1rem' }}>
        <input type="file" accept="image/*" multiple onChange={e => addFotos(`c${idx}`, e.target.files)} />
        <div className="pz-t">📷 Fotos comparable {idx} (opcional)</div>
      </div>
      {renderFotos(comp.fotos, `c${idx}`)}
    </div>
    );
  };

  // ─── RENDER GAUGE SVG ─────────────────────────────────────────────────────

  const renderGauge = (vuPromedio) => {
    const [vuMin, vuMax] = getRangoVU(zona, tipo);
    const total = vuMax - vuMin || 1;
    const pct = Math.min(1, Math.max(0, ((parseFloat(vuPromedio) || 0) - vuMin) / total));
    // El arco SVG va de 180° (izq) → 270° (tope) → 360°/0° (der)
    const angle = 180 + pct * 180;
    const needleX = 68 + 52 * Math.cos((angle * Math.PI) / 180);
    const needleY = 74 + 52 * Math.sin((angle * Math.PI) / 180);
    const color = pct < 0.3 ? '#cc2222' : pct < 0.7 ? '#c97c0a' : '#0d9e6e';
    const motivo = pct < 0.3
      ? 'El valor unitario está por debajo del rango esperado para la zona. Revisá estado, antigüedad y acceso del comparable.'
      : pct < 0.7
      ? 'El valor unitario está dentro del rango típico de la zona. El precio es consistente con el mercado.'
      : 'El valor unitario supera el rango esperado. La propiedad tiene características superiores al promedio del sector.';
    return (
      <div className="gauge-wrap">
        <svg className="gauge-svg" viewBox="0 0 136 80">
          <path d="M10,74 A58,58 0 0,1 126,74" fill="none" stroke="#e4e5f0" strokeWidth="12" strokeLinecap="round" />
          <path d="M10,74 A58,58 0 0,1 126,74" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={`${pct * 182} 182`} />
          <line x1="68" y1="74" x2={needleX} y2={needleY} stroke="#1e2040" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="68" cy="74" r="5" fill="#1e2040" />
        </svg>
        <div className="g-l">Posición en el mercado</div>
        <div className="g-z" style={{ color }}>{pct < 0.3 ? '🔴 Bajo' : pct < 0.7 ? '🟡 Medio' : '🟢 Alto'}</div>
        <div style={{ fontSize: 10.5, color: 'var(--mid)', lineHeight: 1.5, marginTop: '.4rem', maxWidth: 200, textAlign: 'center' }}>{motivo}</div>
      </div>
    );
  };

  // ─── RENDER RESULTADO · VISTA PROPIETARIO (informe limpio · 4 páginas) ─────
  // Layout amigable pensado para el cliente: sin fórmulas ni coeficientes,
  // con el valor revelado recién en la última página. Reutiliza el mismo estado
  // que la vista agente (fotos, comparables, textos editables, resultado).

  const renderResultadoPropietario = () => {
    if (!resultado) return null;
    const { vrc, rangoMin, rangoMax, supHomProp } = resultado;
    const precioPub = roundToNice(vrc * 1.05);
    const agenteRow = AGENTS.find(a => a.name === agente);
    const agenteRole = agenteRow?.role || userDetails?.type || user?.type || 'Agente';
    const agenteObj = { name: agente || '—', role: agenteRole, initials: (agente || '?').substring(0, 2).toUpperCase() };
    const fechaFmt = new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    const validFmt = new Date(new Date(fecha).getTime() + 30 * 86400000).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    const subtitle = (() => {
      if (zona) return zona;
      const parts = (dir || '').split(',').map(s => s.trim()).filter(Boolean);
      return parts[1] || parts[0] || '—';
    })();
    const amLabel = id => (AMENITIES_LIST.find(a => a.id === id)?.label || id);
    const compActivos = [comp1, comp2, ...(showComp3 ? [comp3] : [])].filter(c => parseFloat(c.precio) > 0);
    const heroPhoto = fotosMain[0];
    const galeria = fotosMain.slice(1, 4);

    const aspNumPlazo = parseFloat(precioAsp) || 0;
    let plazoEst = '45 a 90 días';
    if (aspNumPlazo > 0) {
      if (aspNumPlazo <= vrc * 1.05) plazoEst = '30 a 60 días';
      else if (aspNumPlazo <= vrc * 1.10) plazoEst = '60 a 90 días';
      else plazoEst = '90 a 150 días';
    }

    const caracts = [
      dorm && { ic: '🛏️', l: 'Dormitorios', v: dorm },
      ban && { ic: '🚿', l: 'Baños', v: ban },
      coch && { ic: '🚗', l: 'Cocheras', v: coch },
      supHomProp && { ic: '📐', l: 'Superficie', v: `${fmtN(supHomProp)} m²` },
      antiguedad && { ic: '🏗️', l: 'Antigüedad', v: `${antiguedad} años` },
      (zona || subtitle) && { ic: '📍', l: 'Zona', v: zona || subtitle },
    ].filter(Boolean);

    return (
      <div id="rc-prop" className="rc-visible prop-root" ref={resultRef} style={{ marginTop: '2rem' }}>

        {/* ═══ PÁGINA 1 · PORTADA (sin valor) ═══ */}
        <div ref={propPage1Ref} className="prop-page prop-cover">
          <div className="prop-cover-deco1" /><div className="prop-cover-deco2" />
          <div className="prop-cover-top">
            <div className="prop-cover-brand">
              <img src={logo} alt="BY Raíces" className="prop-cover-logo" />
              <div>
                <div className="prop-cover-name">BY Raíces</div>
                <div className="prop-cover-sub">Soluciones Inmobiliarias · Mendoza</div>
              </div>
            </div>
            <div className="prop-cover-badge">Informe de valoración</div>
          </div>

          {heroPhoto ? (
            <div className="prop-cover-photo"><img src={heroPhoto} alt="" /></div>
          ) : (
            <div className="prop-cover-photo prop-cover-photo-empty">
              <img src={logo} alt="BY Raíces" />
            </div>
          )}

          <div className="prop-cover-body">
            <div className="prop-cover-tipo">{tipo || 'Inmueble'}</div>
            <div className="prop-cover-dir">{dir || subtitle}</div>
            <div className="prop-cover-meta">
              <div><span>Propietario</span><strong>{propietario || '—'}</strong></div>
              <div><span>Superficie</span><strong>{supHomProp ? `${fmtN(supHomProp)} m²` : '—'}</strong></div>
              <div><span>Agente</span><strong>{agenteObj.name}</strong></div>
              <div><span>Fecha</span><strong>{fechaFmt}</strong></div>
            </div>
          </div>
          <div className="prop-cover-foot">
            Preparado especialmente para {propietario || 'usted'} · Informe confidencial
          </div>
        </div>

        {/* ═══ PÁGINA 2 · EL INMUEBLE ═══ */}
        <div ref={propPage2Ref} className="prop-page">
          <div className="prop-h">
            <span className="prop-h-k">01</span>
            <div><div className="prop-h-t">Su propiedad</div><div className="prop-h-s">Un vistazo a las características del inmueble</div></div>
          </div>

          {heroPhoto && (
            <div className="prop-gallery" data-pdf-break="prop-gallery">
              <div className="prop-gallery-hero"><img src={heroPhoto} alt="" /></div>
              {galeria.length > 0 && (
                <div className="prop-gallery-grid">
                  {galeria.map((f, i) => <img key={i} src={f} alt="" />)}
                </div>
              )}
            </div>
          )}

          <div className="prop-caracts">
            {caracts.map((c, i) => (
              <div key={i} className="prop-caract">
                <div className="prop-caract-ic">{c.ic}</div>
                <div className="prop-caract-v">{c.v}</div>
                <div className="prop-caract-l">{c.l}</div>
              </div>
            ))}
          </div>

          {amenitiesProp.length > 0 && (
            <div className="prop-card">
              <div className="prop-card-t">Comodidades destacadas</div>
              <div className="prop-amenities">
                {amenitiesProp.map(id => <span key={id} className="prop-amenity">{amLabel(id)}</span>)}
              </div>
            </div>
          )}

          {analisisComp && (
            <div className="prop-card">
              <div className="prop-card-t">Sobre esta propiedad</div>
              <p className="prop-text">{analisisComp}</p>
            </div>
          )}

          <div className="prop-card prop-card-map">
            <div className="prop-card-t">Ubicación</div>
            <div className="prop-loc">📍 {dir || subtitle}{zona ? ` · ${zona}` : ''}</div>
            {pdfMapUrl && (
              <div className="prop-map" style={{ backgroundImage: `url("${pdfMapUrl}")` }} role="img" aria-label="Mapa de ubicación" />
            )}
            {!pdfMapUrl && !pdfMode && mapLocation && (
              <div className="prop-map">
                <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                  <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} zoom={16} center={mapLocation} options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}>
                    <Marker position={mapLocation} />
                  </GoogleMap>
                </LoadScript>
              </div>
            )}
            {!pdfMapUrl && (!mapLocation || pdfMode) && (
              <div className="prop-map prop-map-empty">📍 {dir || 'Mendoza, Argentina'}</div>
            )}
          </div>
        </div>

        {/* ═══ PÁGINA 3 · EL MERCADO ═══ */}
        <div ref={propPage3Ref} className="prop-page">
          <div className="prop-h">
            <span className="prop-h-k">02</span>
            <div><div className="prop-h-t">El mercado</div><div className="prop-h-s">Propiedades similares y cómo valoramos la suya</div></div>
          </div>

          {compActivos.length > 0 && (
            <div className="prop-comps" data-pdf-break="prop-comps">
              {compActivos.map((c, i) => {
                const supH = calcSupHom(c.supCub, c.supSemi, 0.5, c.supTer, 0.5);
                return (
                  <div key={i} className="prop-comp">
                    {c.fotos && c.fotos[0]
                      ? <div className="prop-comp-photo"><img src={c.fotos[0]} alt="" /></div>
                      : <div className="prop-comp-photo prop-comp-photo-empty">🏠</div>}
                    <div className="prop-comp-body">
                      <div className="prop-comp-badge">Similar {i + 1}</div>
                      <div className="prop-comp-dir">{c.dir || 'Propiedad comparable'}</div>
                      <div className="prop-comp-meta">
                        {supH > 0 && <span>{fmtN(supH)} m²</span>}
                        {c.dorm && <span>{c.dorm} dorm.</span>}
                        {c.ban && <span>{c.ban} baños</span>}
                      </div>
                      <div className="prop-comp-precio">{fmt(parseFloat(c.precio))}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {contextoMercado && (
            <div className="prop-card">
              <div className="prop-card-t">Contexto de la zona</div>
              <p className="prop-text">{contextoMercado}</p>
            </div>
          )}

          <div className="prop-metodo" data-pdf-break="prop-metodo">
            <div className="prop-metodo-t">Cómo determinamos el valor</div>
            <div className="prop-metodo-steps">
              <div className="prop-metodo-step">
                <div className="prop-metodo-num">1</div>
                <div><div className="prop-metodo-st">Analizamos su propiedad</div><div className="prop-metodo-sd">Medimos superficie, distribución, estado y comodidades para entender todo lo que ofrece.</div></div>
              </div>
              <div className="prop-metodo-step">
                <div className="prop-metodo-num">2</div>
                <div><div className="prop-metodo-st">Comparamos con el mercado</div><div className="prop-metodo-sd">Estudiamos {compActivos.length || 'varias'} propiedad{compActivos.length === 1 ? '' : 'es'} similar{compActivos.length === 1 ? '' : 'es'} publicada{compActivos.length === 1 ? '' : 's'} en la zona.</div></div>
              </div>
              <div className="prop-metodo-step">
                <div className="prop-metodo-num">3</div>
                <div><div className="prop-metodo-st">Ajustamos por sus particularidades</div><div className="prop-metodo-sd">Consideramos ubicación, antigüedad, categoría y comodidades para llegar a un valor justo y competitivo.</div></div>
              </div>
            </div>
          </div>

          <div className="prop-plan" data-pdf-break="prop-plan">
            <div className="prop-plan-hdr">
              <div className="prop-plan-t">Nuestro plan para vender su propiedad</div>
            </div>
            <div className="prop-plan-grid">
              {PLAN_ACCION_PASOS.map((p, i) => (
                <div key={i} className="prop-plan-item">
                  <div className="prop-plan-num">{String(i + 1).padStart(2, '0')}</div>
                  <div className="prop-plan-it">{p.t}</div>
                  <div className="prop-plan-id">{p.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ PÁGINA 4 · EL VALOR ═══ */}
        <div ref={propPage4Ref} className="prop-page">
          <div className="prop-h">
            <span className="prop-h-k">03</span>
            <div><div className="prop-h-t">El valor</div><div className="prop-h-s">Nuestra recomendación profesional</div></div>
          </div>

          <div className="prop-value" data-pdf-break="prop-value">
            <div className="prop-value-label">Valor Real Competitivo</div>
            <div className="prop-value-num">{fmt(vrc)}</div>
            <div className="prop-value-range">Rango estimado · {fmt(rangoMin)} — {fmt(rangoMax)}</div>
            <div className="prop-value-cards">
              <div className="prop-value-card"><span>Precio sugerido de publicación</span><strong>{fmt(precioPub)}</strong></div>
              <div className="prop-value-card"><span>Plazo estimado de venta</span><strong>{plazoEst}</strong></div>
              <div className="prop-value-card"><span>Válido hasta</span><strong>{validFmt}</strong></div>
            </div>
          </div>

          {recomendacion && (
            <div className="prop-card">
              <div className="prop-card-t">Nuestra recomendación</div>
              <p className="prop-text">{recomendacion}</p>
            </div>
          )}

          <div className="prop-next" data-pdf-break="prop-next">
            <div className="prop-card-t">Próximos pasos</div>
            <div className="prop-next-grid">
              <div className="prop-next-item"><div className="prop-next-num">1</div><div className="prop-next-t">Revisemos juntos el informe</div></div>
              <div className="prop-next-item"><div className="prop-next-num">2</div><div className="prop-next-t">Definamos el precio de publicación</div></div>
              <div className="prop-next-item"><div className="prop-next-num">3</div><div className="prop-next-t">Comenzamos la difusión</div></div>
            </div>
          </div>

          <div className="prop-sign" data-pdf-break="prop-sign">
            <div className="prop-sign-av">{agenteObj.initials}</div>
            <div className="prop-sign-body">
              <div className="prop-sign-role">Su agente en BY Raíces</div>
              <div className="prop-sign-name">{agenteObj.name}</div>
              {criterioProfesional && <p className="prop-sign-note">{criterioProfesional}</p>}
              <div className="prop-sign-sig">{agenteObj.name.split(' ')[0]}</div>
            </div>
          </div>

          <div className="prop-contact">
            <img src={logo} alt="BY Raíces" className="prop-contact-logo" />
            <div className="prop-contact-body">
              <div className="prop-contact-t">BY Raíces · Soluciones Inmobiliarias</div>
              <div className="prop-contact-s">byraices.com · @byraices · Mendoza, Argentina</div>
            </div>
          </div>

          <div className="prop-disc">
            Este informe es una estimación profesional de valor de mercado emitida por BY Raíces, con vigencia de 30 días desde su emisión. No constituye un avalúo pericial. Responsable: {CORREDORA_RESPONSABLE.name} · {CORREDORA_RESPONSABLE.matricula}.
          </div>
        </div>

      </div>
    );
  };

  // ─── RENDER RESULTADO ────────────────────────────────────────────────────

  const renderResultado = () => {
    if (!resultado) return null;
    const { vrc, vus, vuPromedio, rangoMin, rangoMax, zonaLabel, supHomProp, rhFactor, hnCoef, pisoCoef = 1, amPropCoef = 1, oriCoef = 1, catPropCoef = 1, obsfCoef = 1 } = resultado;
    const precioPub = roundToNice(vrc * 1.05);
    const brechaPct = 0.08; // Mendoza: brecha publicación→venta real estimada ~8%
    const precioVentaReal = roundToNice(precioPub * (1 - brechaPct));
    const agenteRow = AGENTS.find(a => a.name === agente);
    const agenteRole = agenteRow?.role || userDetails?.type || user?.type || 'Agente';
    const agenteObj = { name: agente || '—', role: agenteRole, initials: (agente || '?').substring(0, 2).toUpperCase() };
    const fechaFmt = new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    const validFmt = new Date(new Date(fecha).getTime() + 30 * 86400000).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    // VU/m² del inmueble tasado (sale del propio VRC).
    const vuPropPorM2 = supHomProp > 0 ? vrc / supHomProp : 0;
    const brechaVsProm = vuPromedio > 0 ? ((vuPropPorM2 / vuPromedio) - 1) * 100 : 0;
    // Posición en el rango de comparables: dónde cae el VU/m² del inmueble dentro
    // del rango de los comparables (ampliado ±5% para permitir salir arriba/abajo).
    const vuMinComp = vus.length ? Math.min(...vus) : 0;
    const vuMaxComp = vus.length ? Math.max(...vus) : 1;
    const vuLo = vuMinComp * 0.95;
    const vuHi = vuMaxComp * 1.05;
    const gaugePct = vuHi > vuLo
      ? Math.round(Math.max(0, Math.min(1, (vuPropPorM2 - vuLo) / (vuHi - vuLo))) * 100)
      : 50;
    const gaugeLabel = gaugePct >= 70 ? 'Posición alta' : gaugePct >= 45 ? 'Bien posicionado' : 'Posición baja';
    // Validación cruzada: dispersión entre comparables
    const dispersion = vus.length > 1 ? ((Math.max(...vus) / Math.min(...vus)) - 1) * 100 : 0;

    // Calidad de la tasación. Con 2 comparables ya alcanza para confianza alta;
    // un 3er comparable es opcional. Solo bajamos confianza si hay 1 solo,
    // si la dispersión es muy alta o si los ajustes técnicos superan ±15%.
    const clamped = !!resultado.clamped;
    const compCount = vus.length;
    let qualityLevel = 'high';
    let qualityLabel = 'Alta confianza';
    let qualityColor = '#10b981'; // verde
    let qualityReasons = [];
    if (compCount < 2) {
      qualityLevel = 'low'; qualityLabel = 'Confianza baja'; qualityColor = '#dc2626';
      qualityReasons.push('Solo 1 comparable analizado');
    }
    if (dispersion > 30) {
      qualityLevel = 'low'; qualityLabel = 'Confianza baja'; qualityColor = '#dc2626';
      qualityReasons.push(`Dispersión alta entre comparables (${dispersion.toFixed(0)}%)`);
    } else if (dispersion > 18 && qualityLevel === 'high') {
      qualityLevel = 'med'; qualityLabel = 'Confianza media'; qualityColor = '#f59e0b';
      qualityReasons.push(`Dispersión moderada (${dispersion.toFixed(0)}%)`);
    }
    if (clamped && qualityLevel === 'high') {
      qualityLevel = 'med'; qualityLabel = 'Confianza media'; qualityColor = '#f59e0b';
      qualityReasons.push('Ajustes técnicos topados al ±15%');
    } else if (clamped) {
      qualityReasons.push('Ajustes técnicos topados al ±15%');
    }
    if (qualityReasons.length === 0) qualityReasons.push(`${compCount} comparables · dispersión baja · sin tope técnico`);

    // Plazo estimado dinámico según brecha con precio aspiracional.
    const aspNumPlazo = parseFloat(precioAsp) || 0;
    let plazoEst = '45 a 90 días';
    if (aspNumPlazo > 0) {
      if (aspNumPlazo <= vrc * 1.05) plazoEst = '30 a 60 días';
      else if (aspNumPlazo <= vrc * 1.10) plazoEst = '60 a 90 días';
      else plazoEst = '90 a 150 días';
    }

    // Subtitle: barrio o zona
    const subtitle = (() => {
      if (zona) return zona;
      const parts = (dir || '').split(',').map(s => s.trim()).filter(Boolean);
      return parts[1] || parts[0] || '—';
    })();

    return (
      <div id="rc" className="rc-visible" ref={resultRef} style={{ marginTop: '2rem' }}>

        {/* ── PDF PAGE 2: Cover gradient + Resumen 3 cards + Foto hero + Stat cards + Aspiracional ── */}
        <div ref={page2Ref}>

        {/* COVER gradient — rediseño Liliana */}
        <div className="r-cover r-cover-v2">
          <div className="rcd1" /><div className="rcd2" />
          <div className="rc-chip-top">
            BY RAÍCES · SOLUCIONES INMOBILIARIAS · MENDOZA{vrcCode ? ` · ${vrcCode}` : ''}
          </div>
          <div className="rc-title-block">
            <div className="rc-bdg">VRC PRO</div>
            <div className="rc-title">VRC · {tipo || 'Inmueble'}</div>
            <div className="rc-addr-v2">{subtitle}</div>
          </div>
          <div className="rc-metas-4">
            <div className="rcm"><span className="rcm-l">Propietario</span><strong>{propietario || '—'}</strong></div>
            <div className="rcm"><span className="rcm-l">Agente</span><strong>{agenteObj.name}</strong></div>
            <div className="rcm"><span className="rcm-l">Tipo</span><strong>{tipo || '—'}</strong></div>
            <div className="rcm"><span className="rcm-l">Fecha</span><strong>{fechaFmt}</strong></div>
          </div>
          <div className="rc-bar-v2">
            <div className="rcbv-vrc">
              <span>Valor Real Competitivo</span>
              <strong>{fmt(vrc)}</strong>
            </div>
            <div className="rcbv-val">
              <span>Válido hasta</span>
              <strong>{validFmt}</strong>
            </div>
          </div>
          <img src={logo} alt="BY Raíces" className="rc-logo-fixed" />
        </div>

        {/* BADGE CALIDAD DE TASACIÓN — solo en pantalla, oculto en PDF */}
        <div className="quality-badge-wrap agent-only no-pdf" data-pdf-break="quality-badge">
          <div className="quality-badge" style={{ borderLeftColor: qualityColor }}>
            <div className="qb-dot" style={{ background: qualityColor }} />
            <div className="qb-body">
              <div className="qb-title" style={{ color: qualityColor }}>{qualityLabel}</div>
              <div className="qb-sub">{qualityReasons.join(' · ')}</div>
            </div>
            <div className="qb-meta">
              <span className="qb-meta-k">Comparables</span>
              <span className="qb-meta-v">{compCount}</span>
              <span className="qb-meta-k">Dispersión</span>
              <span className="qb-meta-v">{dispersion.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* RESUMEN EJECUTIVO · 3 cards (sin VU Promedio) */}
        <div className="resumen-exec">
          <div className="re-header">
            <span className="re-header-icon">📊</span>
            <div>
              <div className="re-header-t">Resumen Ejecutivo · VRC PRO</div>
              <div className="re-header-s">{tipo || '—'} · {subtitle} · {fechaFmt}</div>
            </div>
          </div>
          <div className="re-body re-body-3">
            <div className="re-item highlight">
              <div className="re-item-icon">🏠</div>
              <div className="re-item-label">Valor Real Competitivo</div>
              <div className="re-item-val">{fmt(vrc)}</div>
              <div className="re-item-sub">Rango {fmt(rangoMin)} — {fmt(rangoMax)}</div>
            </div>
            <div className="re-item">
              <div className="re-item-icon">🏷️</div>
              <div className="re-item-label">Precio de Publicación</div>
              <div className="re-item-val">{fmt(precioPub)}</div>
              <div className="re-item-sub">VRC +5% (margen negociación)</div>
            </div>
            <div className="re-item">
              <div className="re-item-icon">📐</div>
              <div className="re-item-label">Sup. Homogeneizada</div>
              <div className="re-item-val">{fmtN(supHomProp)} m²</div>
              <div className="re-item-sub">Base del cálculo</div>
            </div>
          </div>
          <div className="re-puntos">
            <div className="re-punto g">
              <div className="re-punto-t">🏠 Inmueble</div>
              {tipo || 'Inmueble'} de {fmtN(supHomProp)} m² homogeneizados en zona {zona || '—'}.
            </div>
            <div className="re-punto">
              <div className="re-punto-t">📊 Mercado</div>
              {zonaLabel} · {vus.length} comparable{vus.length > 1 ? 's' : ''} analizado{vus.length > 1 ? 's' : ''}.
            </div>
            <div className="re-punto w">
              <div className="re-punto-t">⏱ Plazo estimado</div>
              {plazoEst} según mercado y estrategia de publicación.
            </div>
          </div>
        </div>

        {/* FOTO HERO DEL INMUEBLE */}
        {fotosMain[0] && (
          <div className="hero-photo" data-pdf-break="hero-photo">
            <img src={fotosMain[0]} alt="" />
            <div className="hero-photo-label">{subtitle}</div>
            <div className="hero-photo-badge">↑ Inmueble</div>
          </div>
        )}

        {/* 3 STAT CARDS · brecha vs prom + publicación + venta real */}
        <div className="stat-cards-3">
          <div className="stc">
            <div className="stc-l">VRC vs prom. comparables</div>
            <div className={`stc-v ${brechaVsProm >= 0 ? 'pos' : 'neg'}`}>
              {brechaVsProm >= 0 ? '+' : ''}{brechaVsProm.toFixed(1)}%
            </div>
            <div className="stc-s">{fmt(vuPropPorM2)}/m² inmueble · {fmt(vuPromedio)}/m² prom.</div>
          </div>
          <div className="stc">
            <div className="stc-l">Precio de publicación</div>
            <div className="stc-v stc-blue">{fmt(precioPub)}</div>
            <div className="stc-s">VRC +5% · margen negociación</div>
          </div>
          <div className="stc">
            <div className="stc-l">Precio venta real estimado</div>
            <div className="stc-v stc-wine">{fmt(precioVentaReal)}</div>
            <div className="stc-s">−{(brechaPct * 100).toFixed(0)}% brecha Mendoza · cierre más probable</div>
          </div>
        </div>

        {/* BANNER ASPIRACIONAL INLINE */}
        {precioAsp && (() => {
          const aspNum = parseFloat(precioAsp);
          const diff = aspNum - vrc;
          const diffPct = (((aspNum / vrc) - 1) * 100);
          const isOver = diff > 0;
          return (
            <div className={`asp-inline ${isOver ? 'over' : 'under'}`} data-pdf-break="aspiracional">
              <div className="asp-l">
                <span className="asp-tag">🎯 Precio aspiracional del propietario</span>
                <strong>{fmt(aspNum)}</strong>
              </div>
              <span className="asp-arrow">→</span>
              <div className="asp-r">
                <span className="asp-tag">Diferencia con el VRC</span>
                <strong>{isOver ? '+' : ''}{fmt(Math.abs(diff))} <em>({diffPct.toFixed(1)}%)</em></strong>
              </div>
            </div>
          );
        })()}

        </div>{/* end page 2 */}

        {/* ── PDF PAGE 3: Brecha + VRC Hero gauge% + Validación + Stats + Contexto + Mapa ── */}
        <div ref={page3Ref}>

        {/* BRECHA PUBLICACIÓN ↔ VENTA REAL · MENDOZA */}
        <div className="brecha-wide" data-pdf-break="brecha">
          <div className="bw-l">
            <div className="bw-t">Brecha publicación vs venta real · Mendoza</div>
            <div className="bw-s">Entre el precio publicado y el valor real de cierre estimado (≈{(brechaPct * 100).toFixed(0)}%)</div>
          </div>
          <div className="bw-stats">
            <div className="bws"><span>Publicación</span><strong>{fmt(precioPub)}</strong></div>
            <div className="bws"><span>Brecha</span><strong>{(brechaPct * 100).toFixed(0)}%</strong></div>
            <div className="bws"><span>Venta real est.</span><strong>{fmt(precioVentaReal)}</strong></div>
          </div>
        </div>

        {/* BRECHA con precio aspiracional */}
        {precioAsp && (
          <div className="brecha-wide brecha-wide-asp">
            <div className="bw-l">
              <div className="bw-t">Análisis de brecha · Aspiracional</div>
              <div className="bw-s">Diferencia entre precio aspiracional y VRC calculado</div>
            </div>
            <div className="bw-stats">
              <div className="bws"><span>Aspiracional</span><strong>{fmt(parseFloat(precioAsp))}</strong></div>
              <div className="bws"><span>VRC</span><strong>{fmt(vrc)}</strong></div>
              <div className="bws"><span>Δ</span><strong>{(((parseFloat(precioAsp) / vrc) - 1) * 100).toFixed(1)}%</strong></div>
            </div>
          </div>
        )}

        {/* VRC HERO con gauge porcentaje */}
        <div className="vrc-hero vrc-hero-pct" data-pdf-break="vrc-hero">
          <div className="vh-left">
            <div className="vh-l">Valor Real Competitivo</div>
            <div className="vh-v">{fmt(vrc)}</div>
            <div className="vh-range">Rango · {fmt(rangoMin)} — {fmt(rangoMax)}</div>
            {manualMode && manualVRC && (
              <div className="vrc-manual-badge">
                <span>⚙️ VRC ajustado manualmente</span>
                <button type="button" className="vrc-manual-back-btn agent-only" onClick={() => { setManualMode(false); setManualVRC(''); handleCalc(); }}>Volver al VRC calculado</button>
              </div>
            )}
            <div className="chips">
              <span className="chip ch-b">{fmt(vuPropPorM2)}/m²</span>
              <span className="chip ch-w">{fmtN(supHomProp)}m² hom.</span>
              <span className="chip ch-g">Publicar: {fmt(precioPub)}</span>
              {hnCoef !== 1 && <span className="chip ch-g">H-N ×{fmtN(hnCoef, 3)}</span>}
              {rhFactor < 1 && <span className="chip ch-a">R-H ×{fmtN(rhFactor, 3)}</span>}
              {catPropCoef !== 1 && <span className="chip ch-w">Cat. ×{fmtN(catPropCoef, 2)}</span>}
              {amenitiesProp.length > 0 && <span className="chip ch-w">{amenitiesProp.length} amenities</span>}
            </div>
          </div>
          {/* VELOCÍMETRO — posición del VU/m² del inmueble dentro del rango de comparables */}
          {(() => {
            const pct = Math.max(0, Math.min(100, gaugePct)) / 100;
            const angle = 180 + pct * 180;
            const needleX = 68 + 52 * Math.cos((angle * Math.PI) / 180);
            const needleY = 74 + 52 * Math.sin((angle * Math.PI) / 180);
            const gColor = pct < 0.3 ? '#cc2222' : pct < 0.7 ? '#c97c0a' : '#0d9e6e';
            return (
              <div className="vh-gauge vh-gauge-svg">
                <svg className="gauge-svg" viewBox="0 0 136 90">
                  <path d="M10,74 A58,58 0 0,1 126,74" fill="none" stroke="#e4e5f0" strokeWidth="12" strokeLinecap="round" />
                  <path d="M10,74 A58,58 0 0,1 126,74" fill="none" stroke={gColor} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${pct * 182} 182`} />
                  <line x1="68" y1="74" x2={needleX} y2={needleY} stroke="#1e2040" strokeWidth="2.5" strokeLinecap="round" />
                  <circle cx="68" cy="74" r="5" fill="#1e2040" />
                  <text x="68" y="58" textAnchor="middle" fontSize="18" fontWeight="700" fill={gColor} fontFamily="'Cormorant Garamond',serif">{gaugePct}%</text>
                </svg>
                <div className="vg-label" style={{ color: gColor }}>{gaugeLabel}</div>
                <div className="vg-sub">VU/m² del inmueble vs rango de comparables</div>
                <div className="vg-help no-pdf">0% = más barato del rango · 50% = al medio · 100% = más caro</div>
              </div>
            );
          })()}
        </div>

        {/* VALIDACIÓN CRUZADA */}
        {dispersion > 30 && (
          <div className="validation-warn" data-pdf-break="validation">
            ⚠️ <strong>Validación cruzada de comparables</strong> — Los valores unitarios difieren un <strong>{dispersion.toFixed(0)}%</strong> entre comparables. Verificá similitud real con el inmueble tasado para mayor precisión.
          </div>
        )}

        {/* 4 MINI-STATS */}
        <div className="mini-stats-4">
          <div className="ms4">
            <div className="ms4-l">Sup. Homogeneizada</div>
            <div className="ms4-v">{fmtN(supHomProp)} m²</div>
          </div>
          <div className="ms4">
            <div className="ms4-l">V. Unit. Promedio</div>
            <div className="ms4-v">{fmt(vuPromedio)}/m²</div>
          </div>
          {vus[0] != null && (
            <div className="ms4">
              <div className="ms4-l">V. Unit. Comp. 1</div>
              <div className="ms4-v">{fmt(vus[0])}/m²</div>
            </div>
          )}
          {vus[1] != null && (
            <div className="ms4">
              <div className="ms4-l">V. Unit. Comp. 2</div>
              <div className="ms4-v">{fmt(vus[1])}/m²</div>
            </div>
          )}
        </div>

        {/* CONTEXTO DE MERCADO · 3 stats editables + análisis */}
        <div className="mctx">
          <div className="mctx-hdr">
            <div className="mctx-ic">🌍</div>
            <div>
              <div className="mctx-t">Contexto de Mercado · {zona || 'Zona'}</div>
              <div className="mctx-s">Análisis del entorno competitivo</div>
            </div>
          </div>
          <div className="ctx-stats-3">
            <div className="ctxs">
              <div className="ctxs-l">Precio/m² zona</div>
              <input className="ctx-input agent-only" type="text" placeholder="Ej: USD 600/m²" value={ctxPrecioZona} onChange={e => setCtxPrecioZona(e.target.value)} />
              <div className="ctxs-v">{ctxPrecioZona || '—'}</div>
            </div>
            <div className="ctxs">
              <div className="ctxs-l">Competencia activa</div>
              <input className="ctx-input agent-only" type="text" placeholder="Ej: 12 publicaciones" value={ctxCompetencia} onChange={e => setCtxCompetencia(e.target.value)} />
              <div className="ctxs-v">{ctxCompetencia || '—'}</div>
            </div>
            <div className="ctxs">
              <div className="ctxs-l">Índice absorción</div>
              <input className="ctx-input agent-only" type="text" placeholder="Ej: 60-90 días" value={ctxAbsorcion} onChange={e => setCtxAbsorcion(e.target.value)} />
              <div className="ctxs-v">{ctxAbsorcion || '—'}</div>
            </div>
          </div>
          <div className="mctx-body">
            <textarea className="ed" rows={3} placeholder="Analizá el contexto del mercado: demanda, tendencias, plazos típicos en la zona..." value={contextoMercado} onChange={e => setContextoMercado(e.target.value)} />
            <div className="ed-hint">✏️ Editable · Este texto aparece en el informe</div>
            <div className="ed-static">{contextoMercado}</div>
          </div>
        </div>

        {/* UBICACIÓN DEL INMUEBLE con chip barrio */}
        <div className="card">
          <div className="ct">Ubicación del Inmueble</div>
          {(zona || subtitle) && <div className="map-chip-barrio">📍 Barrio {zona || subtitle}</div>}
          {pdfMapUrl && (
            <div
              ref={mapImgRef}
              className="vrc-map-wrap"
              role="img"
              aria-label="Mapa de ubicación"
              style={{
                display: 'block',
                width: 'calc(100% + 3rem)',
                height: pdfMode ? '260px' : '280px',
                margin: '.875rem -1.5rem -1.5rem',
                overflow: 'hidden',
                backgroundColor: 'var(--stone)',
                backgroundImage: `url("${pdfMapUrl}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )}
          {!pdfMapUrl && !pdfMode && mapLocation && (
            <div ref={mapCaptureRef} className="vrc-map-wrap">
              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  zoom={16}
                  center={mapLocation}
                  options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
                >
                  <Marker position={mapLocation} />
                </GoogleMap>
              </LoadScript>
            </div>
          )}
          {!pdfMapUrl && (!mapLocation || pdfMode) && (
            <div className="map-fallback">
              <div style={{ fontSize: 38, lineHeight: 1 }}>📍</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', textAlign: 'center', maxWidth: '80%' }}>{dir || 'Dirección no disponible'}</div>
              {zona && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue)', background: 'var(--bll)', borderRadius: 20, padding: '3px 14px', marginTop: '.2rem' }}>Zona {zona}</div>}
              <div style={{ fontSize: 12, color: 'var(--mid)', marginTop: '.4rem' }}>Mendoza, Argentina</div>
            </div>
          )}
          <div className="hint" style={{ marginTop: '.5rem' }}>{dir || '—'} {zona ? `· Zona ${zona}` : ''}</div>
        </div>

        </div>{/* end page 3 */}

        {/* ── PDF PAGE 4: Tabla Impacto + Chart precios + Header tabla comparativa ── */}
        <div ref={page4Ref}>

        {/* IMPACTO DE CADA COEFICIENTE EN EL VRC */}
        {(() => {
          const base = vuPromedio * supHomProp;
          const ajusteRaw = resultado.ajusteRaw || 1;
          const ajusteClamped = resultado.ajusteClamped || ajusteRaw;
          const clamped = !!resultado.clamped;
          const rows = [
            { label: 'Base de mercado (VU prom × SupHom)', mult: 1, isBase: true },
            { label: 'Orientación', mult: oriCoef },
            { label: 'Categoría constructiva', mult: catPropCoef },
            { label: 'Hofmann-Neill (frente)', mult: hnCoef },
            { label: 'Piso (deptos)', mult: pisoCoef },
            { label: 'Obsolescencia funcional', mult: obsfCoef },
            { label: 'Depreciación Ross-Heidecke', mult: rhFactor },
            { label: 'Amenities del inmueble', mult: amPropCoef },
          ];
          let acc = base;
          const computed = rows.map(r => {
            if (r.isBase) return { ...r, delta: 0, acumulado: acc };
            const prev = acc;
            acc = acc * r.mult;
            return { ...r, delta: acc - prev, acumulado: acc };
          });
          // Si los coef brutos superan ±15%, mostrar fila "Tope técnico aplicado".
          const vrcTecnico = base * ajusteClamped;
          const topeDelta = clamped ? (vrcTecnico - acc) : 0;
          return (
            <div className="r_impact_table card" data-pdf-break="impact-table">
              <div className="ct">💵 Impacto de cada coeficiente en el VRC</div>
              <div style={{ overflowX: 'auto' }}>
                <table className="tc" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Factor</th>
                      <th style={{ textAlign: 'right' }}>Coef.</th>
                      <th style={{ textAlign: 'right' }}>Impacto USD</th>
                      <th style={{ textAlign: 'right' }}>Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {computed.map((r, i) => (
                      <tr key={i} style={{ background: r.isBase ? 'var(--bll)' : 'transparent' }}>
                        <td>{r.label}</td>
                        <td style={{ textAlign: 'right' }}>{r.isBase ? '—' : fmtN(r.mult, 4)}</td>
                        <td style={{ textAlign: 'right', color: r.isBase ? 'var(--mid)' : (r.delta >= 0 ? 'var(--green)' : 'var(--wine)') }}>
                          {r.isBase ? '—' : `${r.delta >= 0 ? '+' : ''}${fmt(r.delta)}`}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.acumulado)}</td>
                      </tr>
                    ))}
                    {clamped && (
                      <tr className="no-pdf" style={{ background: '#fff8e6', fontStyle: 'italic' }}>
                        <td>Tope técnico aplicado (±15% sobre base)</td>
                        <td style={{ textAlign: 'right' }}>{fmtN(ajusteClamped, 4)}</td>
                        <td style={{ textAlign: 'right', color: topeDelta >= 0 ? 'var(--green)' : 'var(--wine)' }}>
                          {`${topeDelta >= 0 ? '+' : ''}${fmt(topeDelta)}`}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(vrcTecnico)}</td>
                      </tr>
                    )}
                    <tr className="impact-final-row" style={{ background: 'var(--blue)', color: '#fff', fontWeight: 700 }}>
                      <td style={{ color: '#fff' }}>VRC Final (ajustado)</td>
                      <td style={{ textAlign: 'right', color: '#fff' }}>—</td>
                      <td style={{ textAlign: 'right', color: 'rgba(255,255,255,.7)' }}>—</td>
                      <td style={{ textAlign: 'right', color: '#fff' }}>{fmt(vrc)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="no-pdf" style={{ fontSize: 11, color: 'var(--mid)', marginTop: '.5rem' }}>
                Lectura: el <b>valor comparativo de mercado</b> es la base; los coeficientes técnicos afinan en ±15% como máximo.
                {clamped && <span style={{ color: 'var(--amb)' }}> Se aplicó el tope porque los ajustes superaban la banda.</span>}
              </div>
            </div>
          );
        })()}

        {/* CHART COMPARATIVA DE PRECIOS (USD) */}
        <div className="chart-precios" data-pdf-break="chart-precios">
          <div className="ct">📊 Comparativa de precios (USD)</div>
          <div className="cp-bars">
            {(() => {
              const items = [
                { label: 'Inmueble · VRC', value: vrc, color: 'var(--blue)' },
                ...[comp1, comp2, ...(showComp3 ? [comp3] : [])]
                  .filter(c => parseFloat(c.precio) > 0)
                  .map((c, i) => ({ label: `Comparable ${i + 1}`, value: parseFloat(c.precio), color: i === 0 ? 'var(--wine)' : i === 1 ? 'var(--green)' : 'var(--amb)' }))
              ];
              const max = Math.max(...items.map(it => it.value)) * 1.1;
              return items.map((it, i) => (
                <div key={i} className="cpb">
                  <div className="cpb-bar"><div className="cpb-fill" style={{ width: `${(it.value / max) * 100}%`, background: it.color }} /></div>
                  <div className="cpb-label">{it.label}</div>
                  <div className="cpb-val">{fmt(it.value)}</div>
                </div>
              ));
            })()}
          </div>
          {/* canvas oculto requerido por el efecto que aún lo dibuja */}
          <canvas id="bChart" ref={chartRef} style={{ display: 'none', width: 1, height: 1 }} />
        </div>

        {/* TABLA COMPARATIVA DE INMUEBLES — bloque principal (datos + coeficientes + VU) */}
        <div className="tabla-comp" data-pdf-break="tabla-comparativa">
          <div className="ct">📋 Tabla comparativa de inmuebles</div>
          {(() => {
            const cps = [
              { label: 'Inmueble', accent: 'var(--blue)', data: {
                dir: dir || '—',
                precio: '—',
                supCub, supSemi, supTer, supHom: supHomProp,
                dorm, ban, antig: antiguedad,
                estadoLbl: '—', estadoCoef: '—',
                antCoef: '—', neg: '—', tiempo: '—', ubic: '—',
                fechaCoef: '—', similitud: '—', coefTotal: '—',
                vuBruto: '—', vuAjustado: '—',
              }},
              ...[comp1, comp2, ...(showComp3 ? [comp3] : [])]
                .filter(c => parseFloat(c.precio) > 0)
                .map((c, i) => {
                  const supH = calcSupHom(c.supCub, c.supSemi, 0.5, c.supTer, 0.5);
                  const vuB = supH > 0 ? parseFloat(c.precio) / supH : 0;
                  const fc = fechaCoef(c.fechaPub);
                  const eC = parseFloat(c.estado) || 1;
                  const aC = parseFloat(c.antCoef) || 1;
                  const nC = parseFloat(c.neg) || 1;
                  const tC = parseFloat(c.tiempo) || 1;
                  const uC = parseFloat(c.ubic) || 1;
                  const coefTot = eC * aC * nC * tC * uC * fc;
                  const vuA = vuB * coefTot;
                  const sim = vus[i] && vuPromedio ? (100 - Math.abs((vus[i] / vuPromedio - 1) * 100)).toFixed(0) + '%' : '—';
                  return {
                    label: `C${i + 1}`,
                    accent: i === 0 ? 'var(--wine)' : i === 1 ? 'var(--green)' : 'var(--amb)',
                    data: {
                      dir: c.dir || '—',
                      precio: parseFloat(c.precio),
                      supCub: c.supCub, supSemi: c.supSemi, supTer: c.supTer, supHom: supH,
                      dorm: c.dorm, ban: c.ban, antig: c.ant,
                      estadoLbl: c.estadoLbl || c.estado || '—',
                      estadoCoef: c.estado || '—',
                      antCoef: c.antCoef || '—',
                      neg: c.neg || '—',
                      tiempo: c.tiempo || '—',
                      ubic: c.ubic || '—',
                      fechaCoef: fmtN(fc, 3),
                      similitud: sim,
                      coefTotal: fmtN(coefTot, 3),
                      vuBruto: vuB,
                      vuAjustado: vuA,
                    },
                  };
                })
            ];
            const featuresA = [
              { k: 'Dirección', f: d => d.dir },
              { k: 'Precio publicado', f: d => typeof d.precio === 'number' ? fmt(d.precio) : d.precio },
              { k: 'Sup. cubierta', f: d => d.supCub ? `${fmtN(parseFloat(d.supCub))} m²` : '—' },
              { k: 'Sup. semicubierta', f: d => d.supSemi ? `${fmtN(parseFloat(d.supSemi))} m²` : '—' },
              { k: 'Sup. terreno', f: d => d.supTer ? `${fmtN(parseFloat(d.supTer))} m²` : '—' },
              { k: 'Sup. homogeneizada', f: d => d.supHom ? `${fmtN(d.supHom)} m²` : '—' },
              { k: 'Dormitorios', f: d => d.dorm || '—' },
              { k: 'Baños', f: d => d.ban || '—' },
              { k: 'Antigüedad', f: d => d.antig ? `${d.antig} años` : '—' },
              { k: 'Estado', f: d => d.estadoLbl || '—' },
              { k: 'Coef. estado', f: d => d.estadoCoef || '—' },
              { k: 'Coef. antigüedad', f: d => d.antCoef || '—' },
              { k: 'Negociabilidad', f: d => d.neg || '—' },
              { k: 'Tiempo mercado', f: d => d.tiempo || '—' },
              { k: 'Ubicación relativa', f: d => d.ubic || '—' },
              { k: 'Coef. fecha', f: d => d.fechaCoef || '—' },
              { k: 'Similitud (peso)', f: d => d.similitud || '—' },
              { k: 'Coef. total', f: d => d.coefTotal || '—' },
              { k: 'V. Unitario bruto', f: d => typeof d.vuBruto === 'number' ? `${fmt(d.vuBruto)}/m²` : d.vuBruto },
              { k: 'V. Unitario ajustado', cn: 'tc-eq', f: d => typeof d.vuAjustado === 'number' ? `${fmt(d.vuAjustado)}/m²` : d.vuAjustado },
            ];
            return (
              <div className="tabla-comp-wrap">
                <table className="tabla-comp-tbl">
                  <thead>
                    <tr>
                      <th>Característica</th>
                      {cps.map((c, i) => <th key={i} style={{ borderTop: `3px solid ${c.accent}` }}>{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {featuresA.map((row, i) => (
                      <tr key={i} className={row.cn || ''}>
                        <td className="tc-key">{row.k}</td>
                        {cps.map((c, j) => <td key={j}>{row.f(c.data)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        </div>{/* end page 4 */}

        {/* ── PDF PAGE 5: Tabla cont + Fotos comparables + Cómo llegamos + FODA + Análisis ── */}
        <div ref={page5Ref}>

        {/* TABLA COMPARATIVA · cierre (factores propios + categoría / acceso / amenities) */}
        <div className="tabla-comp tabla-comp-cont">
          {(() => {
            const cps = [
              { label: 'Inmueble', accent: 'var(--blue)', data: {
                cat: catProp || '—', acceso: '—',
                amenN: amenitiesProp.length,
                venta: 'Tasación',
                catAccAm: '—',
                hn: hnCoef,
              }},
              ...[comp1, comp2, ...(showComp3 ? [comp3] : [])]
                .filter(c => parseFloat(c.precio) > 0)
                .map((c, i) => {
                  const cat = parseFloat(c.cat) || 1;
                  const acc = parseFloat(c.acceso) || 1;
                  const am = calcAmCoef(c.amenities || []);
                  return {
                    label: `C${i + 1}`,
                    accent: i === 0 ? 'var(--wine)' : i === 1 ? 'var(--green)' : 'var(--amb)',
                    data: {
                      cat: c.cat || '—',
                      acceso: c.acceso || '—',
                      amenN: (c.amenities || []).length,
                      venta: c.venta || 'En venta',
                      catAccAm: fmtN((1 / cat) * acc * am, 3),
                      hn: 1,
                    },
                  };
                })
            ];
            const featuresB = [
              { k: 'Estado de venta', f: d => d.venta || '—' },
              { k: 'Categoría constructiva', f: d => d.cat || '—' },
              { k: 'Acceso', f: d => d.acceso || '—' },
              { k: 'Amenities', f: d => d.amenN > 0 ? `${d.amenN} comparables` : '—' },
              { k: 'Coef. cat / acceso / am', f: d => d.catAccAm || '—' },
              { k: 'Hoffman-Neill', f: d => fmtN(d.hn, 4) },
            ];
            return (
              <div className="tabla-comp-wrap">
                <table className="tabla-comp-tbl">
                  <thead>
                    <tr>
                      <th>Característica</th>
                      {cps.map((c, i) => <th key={i} style={{ borderTop: `3px solid ${c.accent}` }}>{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {featuresB.map((row, i) => (
                      <tr key={i}>
                        <td className="tc-key">{row.k}</td>
                        {cps.map((c, j) => <td key={j}>{row.f(c.data)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        {/* FOTOS COMPARABLES GRANDES side-by-side */}
        {[comp1, comp2, ...(showComp3 ? [comp3] : [])].some(c => c.fotos.length > 0) && (
          <div className="fotos-comp-big" data-pdf-break="fotos-comparables-big">
            <div className="ct">📷 Fotos de los comparables</div>
            <div className={`fcb-grid fcb-${[comp1, comp2, ...(showComp3 ? [comp3] : [])].filter(c => c.fotos.length > 0).length}`}>
              {[comp1, comp2, ...(showComp3 ? [comp3] : [])].map((c, i) =>
                c.fotos[0] ? (
                  <div key={i} className="fcb-cell">
                    <img src={c.fotos[0]} alt="" />
                    <div className="fcb-overlay">
                      <span className="fcb-badge">C{i + 1}</span>
                      <span className="fcb-dir">{c.dir || `Comparable ${i + 1}`}</span>
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* FODA · sólo si tiene contenido o estamos en pantalla agente */}
        {(foda.f || foda.o || foda.d || foda.a || !pdfMode) && (
          <div className="acard-full" data-pdf-break="foda">
            <h3>Análisis F.O.D.A. del Inmueble</h3>
            <div className="foda">
              <div className="fi ff">
                <h4>💪 Fortalezas</h4>
                <textarea className="fe" maxLength={400} placeholder="Ej: Ubicación central, buen estado, amenities..." value={foda.f} onChange={e => setFoda(p => ({ ...p, f: e.target.value }))} />
                <div className="fchar">{(foda.f || '').length}/400</div>
                <div className="pre fe-s">{(foda.f || '').slice(0, 400)}</div>
              </div>
              <div className="fi fo">
                <h4>🌱 Oportunidades</h4>
                <textarea className="fe" maxLength={400} placeholder="Ej: Zona en revalorización, potencial subdivisión..." value={foda.o} onChange={e => setFoda(p => ({ ...p, o: e.target.value }))} />
                <div className="fchar">{(foda.o || '').length}/400</div>
                <div className="fe-s">{(foda.o || '').slice(0, 400)}</div>
              </div>
              <div className="fi fd">
                <h4>⚠️ Debilidades</h4>
                <textarea className="fe" maxLength={400} placeholder="Ej: Antigüedad, distribución, servicios limitados..." value={foda.d} onChange={e => setFoda(p => ({ ...p, d: e.target.value }))} />
                <div className="fchar">{(foda.d || '').length}/400</div>
                <div className="fe-s">{(foda.d || '').slice(0, 400)}</div>
              </div>
              <div className="fi fa">
                <h4>🔴 Amenazas</h4>
                <textarea className="fe" maxLength={400} placeholder="Ej: Sobreoferta en zona, mercado a la baja..." value={foda.a} onChange={e => setFoda(p => ({ ...p, a: e.target.value }))} />
                <div className="fchar">{(foda.a || '').length}/400</div>
                <div className="fe-s">{(foda.a || '').slice(0, 400)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Análisis del inmueble + Recomendación */}
        {(analisisComp || recomendacion || !pdfMode) && (
          <div className="a2">
            <div className="acard">
              <h3>Análisis del inmueble</h3>
              <textarea className="ed" maxLength={450} placeholder="Describí el inmueble: estado, características, lo que suma y lo que resta..." value={analisisComp} onChange={e => setAnalisisComp(e.target.value)} />
              <div className="ed-hint">✏️ Editable · {(analisisComp || '').length}/450</div>
              <div className="ed-static">{(analisisComp || '').slice(0, 450)}</div>
            </div>
            <div className="acard">
              <h3>Recomendación</h3>
              <textarea className="ed" maxLength={450} placeholder="Recomendación profesional sobre precio de lista, estrategia de venta..." value={recomendacion} onChange={e => setRecomendacion(e.target.value)} />
              <div className="ed-hint">✏️ Editable · {(recomendacion || '').length}/450</div>
              <div className="ed-static">{(recomendacion || '').slice(0, 450)}</div>
            </div>
          </div>
        )}

        {/* PRÓXIMOS PASOS — solo pantalla */}
        <div className="proximos agent-only">
          <div className="proximos-t">Próximos Pasos Recomendados</div>
          <div className="proximos-grid">
            <div className="prox-item">
              <div className="prox-num">01</div>
              <div className="prox-t">Presentar el informe</div>
              <div className="prox-sub">Compartí el VRC con el propietario y analizá juntos la estrategia de precio.</div>
            </div>
            <div className="prox-item">
              <div className="prox-num">02</div>
              <div className="prox-t">Definir precio de lista</div>
              <div className="prox-sub">Acordar el precio de publicación dentro del rango del VRC para maximizar consultas.</div>
            </div>
            <div className="prox-item">
              <div className="prox-num">03</div>
              <div className="prox-t">Actualizar en 30 días</div>
              <div className="prox-sub">El VRC tiene vigencia de 30 días. Revisarlo si no hay ofertas o cambia el mercado.</div>
            </div>
          </div>
        </div>

        </div>{/* end page 5 */}

        {/* ── PDF PAGE 6: Fórmula + Agente + Plan de Acción + Comercial + Descargo ── */}
        <div ref={page6Ref} className="pdf-page-6">

        {/* BLOQUE FÓRMULA */}
        <div className="formula-block" data-pdf-break="formula">
          <div className="fb-t">🧮 Fórmula VRC</div>
          <pre className="fb-code">{`VRC = VU_prom × SupHom
      × Orientación × Categoría
      × Hoffman-Neill × Piso
      × Obs. funcional × R-H
      × Amenities × Ajuste profesional

VRC = ${fmt(vuPromedio)}/m² × ${fmtN(supHomProp)}m²
      × ${fmtN(oriCoef, 2)} × ${fmtN(catPropCoef, 2)}
      × ${fmtN(hnCoef, 3)} × ${fmtN(pisoCoef, 2)}
      × ${fmtN(obsfCoef, 2)} × ${fmtN(rhFactor, 3)}
      × ${fmtN(amPropCoef, 3)} × ${fmtN(VRC_PRESENTATION_UPLIFT, 3)}

VRC = ${fmt(vrc)}`}</pre>
        </div>

        {/* CARD AGENTE · Criterio profesional */}
        <div className="agente-card-pro" data-pdf-break="agente">
          <div className="acp-hdr">
            <div className="acp-av">{agenteObj.initials}</div>
            <div className="acp-meta">
              <div className="acp-role">Agente Responsable · BY Raíces Mendoza</div>
              <div className="acp-name">{agenteObj.name}</div>
              <div className="acp-sub">{agenteObj.role}</div>
            </div>
          </div>
          <div className="acp-body">
            <textarea
              className="ed acp-ed"
              rows={4}
              maxLength={600}
              placeholder="Criterio profesional: redactá tu lectura del inmueble y del mercado, recomendación de estrategia y mensaje al propietario (max 600 caracteres)."
              value={criterioProfesional}
              onChange={e => setCriterioProfesional(e.target.value)}
            />
            <div className="ed-hint">✏️ Editable · {(criterioProfesional || '').length}/600</div>
            <div className="ed-static acp-text">
              {criterioProfesional || (nota || `Como responsable profesional del presente Valor Real Competitivo, considero que el inmueble se encuentra ${gaugeLabel.toLowerCase()} dentro del mercado de la zona de ${zona || 'Mendoza'}. El VRC sugerido contempla la totalidad de los coeficientes técnicos aplicables y refleja un valor de cierre razonable en un plazo estimado de 45 a 90 días.`)}
            </div>
          </div>
          <div className="acp-sig">{agenteObj.name.split(' ')[0]}</div>
          <div className="acp-disc">VRC emitido el {fechaFmt} · Válido hasta {validFmt}{vrcCode ? ` · ${vrcCode}` : ''}</div>
        </div>

        {/* PLAN DE ACCIÓN BY RAÍCES */}
        <div className="plan-accion" data-pdf-break="plan-accion">
          <div className="pa-hdr">
            <div className="pa-t">📋 Plan de Acción BY Raíces</div>
            <div className="pa-honorarios">
              <span>Honorarios</span>
              <strong>{HONORARIOS_DEFAULT}</strong>
            </div>
          </div>
          <div className="pa-steps">
            {PLAN_ACCION_PASOS.map((p, i) => (
              <div key={i} className="pa-step">
                <div className="pa-step-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="pa-step-body">
                  <div className="pa-step-t">{p.t}</div>
                  <div className="pa-step-d">{p.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* BY RAÍCES COMMERCIAL */}
        <div className="comercial-3cards" data-pdf-break="comercial">
          <div className="cc3-hdr">
            <div className="cc3-name">BY Raíces</div>
            <div className="cc3-tag">Soluciones Inmobiliarias · Mendoza, Argentina</div>
          </div>
          <div className="cc3-grid">
            <div className="cc3-card">
              <div className="cc3-t">Lo que hacemos</div>
              <div className="cc3-d">Compra, venta y alquiler de propiedades en Mendoza y Gran Mendoza con estrategia personalizada por inmueble.</div>
            </div>
            <div className="cc3-card">
              <div className="cc3-t">Nuestro método</div>
              <div className="cc3-d">Sistema VRC PRO de tasación profesional basada en comparables reales del mercado y metodología propia auditable.</div>
            </div>
            <div className="cc3-card">
              <div className="cc3-t">Nuestro compromiso</div>
              <div className="cc3-d">Acompañamiento integral desde el primer contacto hasta la posesión, con reporte transparente al propietario.</div>
            </div>
          </div>
          <div className="cc3-cta">
            <div className="cc3-cta-t">¿Querés saber cuánto vale tu propiedad?</div>
            <div className="cc3-cta-u">byraices.com · @byraices</div>
          </div>
        </div>

        {/* FOOTER FIRMA — entre BY Raíces card y descargo */}
        <div className="vrc-pdf-footer" data-pdf-break="footer-firma">
          <div className="vpf-l">
            <div className="vpf-brand">BY Raíces</div>
            <div className="vpf-sub">Soluciones Inmobiliarias · Mendoza, Argentina</div>
            <div className="vpf-sub" style={{ marginTop: 4 }}>Corredora Responsable: <strong>{CORREDORA_RESPONSABLE.name}</strong> · {CORREDORA_RESPONSABLE.matricula}</div>
          </div>
          <div className="vpf-r">
            <div className="vpf-sys">Sistema VRC Pro · desarrollado por</div>
            <div className="vpf-name"><strong>{agenteObj.name}</strong> · {agenteObj.role}</div>
          </div>
        </div>

        {/* DESCARGO PROFESIONAL EXTENSO */}
        <div className="descargo-pro" data-pdf-break="descargo">
          <div className="dp-t">⚖️ Nota de validez y descargo profesional</div>
          <div className="dp-body">
            <p>El presente <strong>Valor Real Competitivo (VRC)</strong> es una estimación profesional emitida por BY Raíces · Soluciones Inmobiliarias, basada en metodología propia de comparables de mercado, coeficientes técnicos (Hoffman-Neill, Ross-Heidecke), ajustes de superficie, antigüedad, categoría constructiva, orientación, ubicación, amenities y demás variables relevadas al momento de la emisión.</p>
            <p>Esta tasación <strong>no constituye un avalúo pericial</strong> en los términos legales aplicables. El valor final de cierre de una operación inmobiliaria puede variar según factores no observables al momento del análisis: estado real de instalaciones ocultas, condiciones dominiales, oferta y demanda puntual, financiación disponible, urgencia del propietario o del comprador, entre otros.</p>
            <p>BY Raíces no se responsabiliza por decisiones comerciales tomadas exclusivamente sobre la base de este informe. Se recomienda complementarlo con la verificación de títulos, libre deuda municipal e impuestos provinciales, y eventualmente una tasación pericial cuando la operación lo requiera (sucesiones, juicios, créditos hipotecarios). <strong>Vigencia del VRC: 30 días corridos</strong> desde la fecha de emisión indicada en la portada del informe.</p>
            <p>Informe emitido bajo la responsabilidad profesional de <strong>{CORREDORA_RESPONSABLE.name}</strong>, {CORREDORA_RESPONSABLE.rol}, matrícula <strong>{CORREDORA_RESPONSABLE.matricula}</strong> (Colegio de Corredores Públicos Inmobiliarios de Mendoza, Ley Provincial Nº 9.001).</p>
          </div>
        </div>

        </div>{/* end page 6 */}

      </div>
    );
  };

  // ─── PORTADA PDF (oculta, se renderiza solo para captura) ──────────────────

  const renderPDFCover = () => {
    if (!resultado) return null;
    const { vrc, rangoMin, rangoMax } = resultado;
    const precioPub = roundToNice(vrc * 1.05);
    const agenteObj = (() => {
      const found = AGENTS.find(a => a.name === agente);
      return {
        name: agente || '—',
        role: found?.role || userDetails?.type || user?.type || 'Agente',
      };
    })();
    const fechaFmt = new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    const validFmt = new Date(new Date(fecha).getTime() + 30 * 86400000).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    const supHom = calcSupHom(supCub, supSemi, coefSemi, supTer, coefTer);
    // Subtitle = barrio / zona (sin la dirección completa)
    const subtitle = (() => {
      if (zona) return zona;
      // si la dirección tiene comas, tomar la segunda parte (típicamente el barrio/localidad)
      const parts = (dir || '').split(',').map(s => s.trim()).filter(Boolean);
      return parts[1] || parts[0] || '—';
    })();
    const supLine = supHom
      ? (supCub ? `${fmtN(supHom)}m² hom. / ${fmtN(parseFloat(supCub))}m² cub.` : `${fmtN(supHom)} m² homogeneizados`)
      : '—';

    return (
      <div className="pdfCover" ref={coverRef} style={{ display: 'none' }}>
        <div className="pc-stripe-left" />
        <div className="pc-stripe-left2" />
        <div className="pc-inner">
          <div className="pc-header">
            <div className="pc-logo-wrap">
              <img src={logo} alt="BY Raíces" className="pc-logo" />
              <div>
                <div className="pc-company">BY Raíces · Soluciones Inmobiliarias</div>
                <div className="pc-company-sub">Mendoza, Argentina</div>
              </div>
            </div>
            <div className="pc-badge-wrap">
              <span className="pc-badge">VRC PRO</span>
              <span className="pc-code">{vrcCode || 'VRC-AAAA-#### (se asigna al guardar)'}</span>
            </div>
          </div>
          <div className="pc-divider" />
          <div className="pc-hero">
            <div className="pc-vrc-label">Valor Real Competitivo</div>
            <div className="pc-vrc-val">{fmt(vrc)}</div>
            <div className="pc-vrc-sub">Rango: {fmt(rangoMin)} — {fmt(rangoMax)} · Publicar: {fmt(precioPub)}</div>
          </div>
          <div className="pc-divider2" />
          <div className="pc-tipo">{tipo || '—'}</div>
          <div className="pc-dir">{subtitle}</div>
          <div className="pc-table">
            <div className="pc-tr"><span className="pc-key">Propietario</span><span className="pc-val">{propietario || '—'}</span></div>
            <div className="pc-tr"><span className="pc-key">Zona</span><span className="pc-val">{zona || '—'}</span></div>
            <div className="pc-tr"><span className="pc-key">Superficie</span><span className="pc-val">{supLine}</span></div>
            <div className="pc-tr"><span className="pc-key">Fecha</span><span className="pc-val">{fechaFmt}</span></div>
            <div className="pc-tr"><span className="pc-key">Válido hasta</span><span className="pc-val">{validFmt}</span></div>
            <div className="pc-tr"><span className="pc-key">Agente</span><span className="pc-val">{agenteObj.name}</span></div>
            <div className="pc-tr"><span className="pc-key">Corredora Resp.</span><span className="pc-val">{CORREDORA_RESPONSABLE.name} · {CORREDORA_RESPONSABLE.matricula}</span></div>
          </div>
        </div>
        <div className="pc-footer">
          <div className="pc-f-left"><strong>BY Raíces · Soluciones Inmobiliarias</strong>Mendoza, Argentina</div>
          <div className="pc-f-right">
            <div>Sistema VRC Pro · <strong>{agenteObj.name}</strong> · {agenteObj.role}</div>
            <div style={{ marginTop: 2, fontSize: '.78em', opacity: .9 }}>Corredora Responsable: <strong>{CORREDORA_RESPONSABLE.name}</strong> · {CORREDORA_RESPONSABLE.matricula}</div>
          </div>
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────

  return (
    <div className={`vrc-pro-root${pdfMode ? ' pdf-mode' : ''}${viewMode === 'prop' ? ' view-prop' : ''}`}>

      {/* HEADER */}
      <header className="vrc-header">
        <div className="hl">
          <img src={logo} alt="BY Raíces" style={{ width: 52, height: 52, objectFit: 'contain', background: '#fff', borderRadius: 9, padding: 3, boxShadow: '0 2px 8px rgba(0,0,0,.15)', flexShrink: 0 }} />
          <div><div className="hl-t">BY Raíces</div><div className="hl-s">Soluciones Inmobiliarias · Mendoza</div></div>
        </div>
        <div className="hr">
        </div>
      </header>

      {/* PORTADA PDF (oculta) */}
      {renderPDFCover()}

      <div className="vrc-main">
        <div className="pg-t">Valor Real Competitivo</div>
        <div className="pg-s">Completá los datos del inmueble y los comparables · el sistema calcula el VRC automáticamente.</div>

        {/* INSTRUCTIVO */}
        <div style={{ marginBottom: '1.75rem' }}>
          <button className="instr-btn" onClick={() => setShowInstr(v => !v)}>
            <span>📖</span>&nbsp; Instructivo · Cómo usar el VRC PRO
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--mid)' }}>{showInstr ? '▲ Ocultar' : '▼ Ver'}</span>
          </button>
          {showInstr && (
            <div className="instr-content">
              <div className="instr-grid">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--blue)', marginBottom: '.875rem' }}>📋 Pasos para completar el VRC</div>
                  {[
                    ['Seleccioná el agente', 'Elegí tu nombre o escribí el nombre del agente responsable del VRC.'],
                    ['Completá los datos del inmueble', 'Tipo, dirección, superficies (cubierta, semicubierta, terreno), características y amenities.'],
                    ['Hoffman-Neill (opcional)', 'Si conocés el frente real del lote, ingresalo junto al frente tipo de la zona (10m).'],
                    ['Subí las fotos', 'La primera foto del inmueble será la imagen principal del informe.'],
                    ['Ingresá los 2 comparables', 'Buscá 2 propiedades similares publicadas en la zona.'],
                    ['Ajustá los coeficientes', 'Ajustá estado, antigüedad, negociabilidad, tiempo en mercado, ubicación, categoría y acceso.'],
                    ['Calculá el VRC', 'Presioná "Calcular VRC". El sistema calcula automáticamente.'],
                    ['Editá el VRC si necesitás', 'Si el número te parece bajo o alto, podés ajustarlo manualmente.'],
                    ['Descargá el PDF', 'El PDF incluye la portada en página 1 y el informe completo.'],
                  ].map(([title, desc], i) => (
                    <div key={i} style={{ display: 'flex', gap: '.75rem', marginBottom: '.75rem' }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: i === 8 ? 'var(--green)' : 'var(--blue)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{title}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--mid)', lineHeight: 1.55 }}>{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--wine)', marginBottom: '.875rem' }}>⚙️ Cómo funciona el cálculo</div>
                  <div style={{ background: 'var(--stone)', borderRadius: 9, padding: '1rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '.5rem' }}>Fórmula VRC</div>
                    <pre style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.9, fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0 }}>
                      {`VU_adj = (precio ÷ supHom)\n  × estado × antigüedad\n  × negociabilidad × tiempo\n  × ubicación × (1÷cat)\n  × acceso × amenities\n\nVRC = VU_prom × supHom\n  × orientación × catProp\n  × hoffmanNeill × piso\n  × obsFuncional × amenProp`}
                    </pre>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--mid)', marginBottom: '.625rem' }}>Coeficientes clave</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', marginBottom: '1rem' }}>
                    {[
                      ['Estado', 'Excelente=1.00 · Buena=0.90 · Regular=0.80'],
                      ['Antigüedad', '0-5a=1.00 · 10-20a=0.90 · 30+a=0.70'],
                      ['Negociabilidad', 'Vendido=1.00 · En venta=0.94 · Urgente=0.85'],
                      ['Acceso', 'Asfaltada=1.00 · Tierra=-4% · Sin svc=-8%'],
                      ['Hoffman-Neill', '√(frente_real ÷ frente_tipo)'],
                      ['Ross-Heidecke', '1 − 1.0646x + 0.7194x² − 0.4588x³ (x=antig/VU)'],
                      ['Fecha publicación', '≤1m=1.00 · ≤3m=1.005 · ≤12m=1.02 · >12m=1.03'],
                      ['Amenities', 'Se informan como atributos; no agregan un porcentaje automático'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: '.5rem' }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink)', minWidth: 120 }}>{k}</span>
                        <span style={{ fontSize: 11.5, color: 'var(--mid)' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: 'var(--bl)', borderRadius: 9, padding: '.875rem', borderLeft: '3px solid var(--blue)' }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--blue)', marginBottom: '.3rem' }}>💡 Tip</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink)', lineHeight: 1.6 }}>Elegí comparables lo más similares posible. Si los valores unitarios difieren más del 30%, el sistema te avisa.</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* WIZARD STEPS */}
        <div className="steps">
          {[
            { n: 1, label: 'Inmueble' },
            { n: 2, label: 'Comparable 1' },
            { n: 3, label: 'Comparable 2' },
            ...(showComp3 ? [{ n: 4, label: 'Comparable 3' }] : []),
            { n: showComp3 ? 5 : 4, label: 'VRC', realStep: 5 },
          ].map(s => (
            <button
              key={s.n}
              className={`sb${step === (s.realStep || s.n) ? ' act' : ''}${(s.realStep || s.n) < step ? ' done' : ''}`}
              onClick={() => setStep(s.realStep || s.n)}
            >
              <span className="sn">{(s.realStep || s.n) < step ? '✓' : s.n}</span>
              <span className="sl">{s.label}</span>
            </button>
          ))}
        </div>

        {/* ══ STEP 1: INMUEBLE ══ */}
        <div className={`sec${step === 1 ? ' act' : ''}`}>

          {/* Guía rápida para el agente — visible solo en pantalla */}
          <div className="quick-help-banner agent-only">
            <div className="qhb-title">📋 Cómo usar esta herramienta</div>
            <ol className="qhb-list">
              <li><b>Paso 1:</b> Cargá el propietario y los datos del inmueble (dirección, superficie, ambientes).</li>
              <li><b>Paso 2:</b> Subí 1 foto principal y elegí la ubicación en el mapa.</li>
              <li><b>Paso 3 y 4:</b> Agregá <b>2 comparables</b> (el 3° es opcional). Datos mínimos: precio, superficie y zona.</li>
              <li><b>Paso 5:</b> Tocá <b>Calcular VRC</b>. Si necesitás ajustar a mano, activá <i>Ajustar VRC manualmente</i>.</li>
            </ol>
            <div className="qhb-tip">💡 Los campos técnicos (categoría, frente, antigüedad) ya vienen con valores estándar: dejalos así si no estás seguro.</div>
          </div>

          {/* Agente — tomado del usuario logeado */}
          <div className="card">
            <div className="ct">Agente Responsable</div>
            <div className="agent-logged">
              <div className="av">{(agente || '?').substring(0, 2).toUpperCase()}</div>
              <div>
                <div className="an">{agente || 'Sin asignar'}</div>
                <div className="ar">{userDetails?.type || user?.type || 'Agente'}</div>
              </div>
            </div>
            <div className="f" style={{ maxWidth: 280, marginTop: '.25rem' }}>
              <label>Nombre del propietario</label>
              <input type="text" placeholder="Ej: Laura Atencia" value={propietario} onChange={e => setPropietario(e.target.value)} />
              <div className="hint">Se autocompleta al vincular un cliente registrado. Podés editarlo manualmente.</div>
            </div>
            {/* Vincular cliente registrado (mismo componente usado en CreateProperty). */}
            {(user?.userId || user?.user?.id) && (
              <div style={{ marginTop: '.5rem' }}>
                <ClientSelector
                  selectedClients={selectedClients}
                  onSelectionChange={(arr) => {
                    const c = Array.isArray(arr) && arr[0];
                    if (c) {
                      setClientId(c.id);
                      const nombre = [c.nombre, c.apellido].filter(Boolean).join(' ').trim()
                        || c.fullName || c.email || '';
                      // Sobrescribir propietario sólo si estaba vacío o si
                      // coincide con el nombre del cliente previo (evita pisar
                      // ediciones manuales del usuario).
                      setPropietario(prev => (!prev || prev === propietarioRef.current) ? nombre : prev);
                      propietarioRef.current = nombre;
                    } else {
                      setClientId('');
                      propietarioRef.current = '';
                    }
                  }}
                  sellerId={user?.userId || user?.user?.id}
                  showCategoryFilter
                  allowCreate
                />
                <div className="hint">Vincular permite ver este VRC desde la ficha del cliente.</div>
              </div>
            )}
            {/* <div style={{ marginTop: '.85rem' }}>
              <button
                type="button"
                className="btn btn-ol"
                onClick={handlePreloadDemo}
                title="Carga un escenario completo de ejemplo para pruebas"
              >
                Precargar demo completa
              </button>
            </div> */}
            {/* <div style={{ marginTop: '.85rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-ol"
                onClick={handlePreloadDemo}
                title="Demo premium — escenario de posición ALTA en zona Residencial"
              >
                🧪 Demo premium
              </button>
              <button
                type="button"
                className="btn btn-w"
                onClick={handlePreloadDemoV2}
                title="Demo TEST v2 — ejercita RH polinómica, fechaCoef, rango Capital, tabla de impacto y chart con 3 comparables"
              >
                🧬 Demo TEST v2 (features nuevas)
              </button>
            </div> */}
          </div>

          {/* Tipo de inmueble */}
          <div className="card">
            <div className="ct">Tipo de Inmueble</div>
            <div className="tt">
              <button className={`tt-b${tipoInm === 'e' ? ' act' : ''}`} onClick={() => setTipoInm('e')}><span className="tt-icon">🏠</span>Casa / Dpto / Local</button>
              <button className={`tt-b${tipoInm === 't' ? ' act' : ''}`} onClick={() => setTipoInm('t')}><span className="tt-icon">🌿</span>Terreno / Lote</button>
            </div>
            <div style={{ marginTop: '.85rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-ol"
                onClick={handlePreloadDemoTerreno}
                title="Demo TERRENO — Lote 800 m² en Maipú con 2 comparables. Precarga todos los datos y comparables para testear el cálculo de terreno."
              >
                🌿 Demo TEST Terreno (Maipú 800 m²)
              </button>
            </div>
            <div className="g2">
              <div className="f"><label>Especificá el tipo</label>
                <input type="text" placeholder="Casa, Departamento, Duplex..." list="vrc-tl" value={tipo} onChange={e => setTipo(e.target.value)} />
                <datalist id="vrc-tl">
                  {['Casa', 'Departamento', 'Duplex', 'PH', 'Local comercial', 'Oficina', 'Terreno urbano', 'Lote'].map(o => <option key={o} value={o} />)}
                </datalist>
              </div>
              <div className="f"><label>Dirección completa</label><input type="text" placeholder="Ej: Belgrano 805, Las Heras, Mendoza" value={dir} onChange={e => setDir(e.target.value)} /></div>
              <div className="f"><label>Zona</label>
                {/* Input con datalist: acepta valores libres (departamentos como
                    'Guaymallén', 'Maipú') además de las categorías estándar.
                    Antes era <select> y filtraba valores seed válidos. */}
                <input
                  type="text"
                  list="vrc-zonas"
                  placeholder="Ej: Residencial, Guaymallén, Maipú…"
                  value={zona}
                  onChange={e => setZona(e.target.value)}
                />
                <datalist id="vrc-zonas">
                  {['Urbana', 'Residencial', 'Comercial', 'Rural', 'Suburbana',
                    'Capital', 'Godoy Cruz', 'Guaymallén', 'Las Heras', 'Maipú',
                    'Lujan de Cuyo', 'San Martín', 'San Rafael'
                  ].map(o => <option key={o} value={o} />)}
                </datalist>
              </div>
              <div className="f"><label>Fecha del VRC</label><input type="date" value={fecha} onChange={e => setFecha(e.target.value)} /></div>
            </div>
          </div>

          {/* Edificado */}
          {tipoInm === 'e' && (
            <div className="card">
              <div className="ct">Características del Inmueble</div>
              <div className="g4">
                <div className="f"><label>Sup. cubierta (m²)</label><div className="ui"><input type="number" min="0" placeholder="0" value={supCub} onChange={e => setSupCub(e.target.value)} style={fieldErrors.supCub || fieldErrors.superficies ? { borderColor: '#dc2626' } : undefined} /><span className="un">m²</span></div></div>
                <div className="f">
                  <label>Sup. semicubierta (m²)</label>
                  <div className="ui"><input type="number" min="0" placeholder="0" value={supSemi} onChange={e => setSupSemi(e.target.value)} style={fieldErrors.supSemi || fieldErrors.superficies ? { borderColor: '#dc2626' } : undefined} /><span className="un">m²</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', marginTop: 3 }}>
                    <label style={{ fontSize: 10, margin: 0, minWidth: 'fit-content' }}>Coef. semi:</label>
                    <select style={{ padding: '4px 6px', fontSize: 11, flex: 1 }} value={coefSemi} onChange={e => setCoefSemi(e.target.value)}>
                      <option value="0.50">0.50 — Estándar</option>
                      <option value="0.55">0.55 — Galería techada</option>
                      <option value="0.60">0.60 — Quincho cerrado</option>
                      <option value="0.65">0.65 — Semicub. alta calidad</option>
                      <option value="0.70">0.70 — Clima cálido / premium</option>
                    </select>
                  </div>
                </div>
                <div className="f">
                  <label>Sup. terreno (m²)</label>
                  <div className="ui"><input type="number" min="0" placeholder="0" value={supTer} onChange={e => setSupTer(e.target.value)} style={fieldErrors.supTer || fieldErrors.superficies ? { borderColor: '#dc2626' } : undefined} /><span className="un">m²</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', marginTop: 3 }}>
                    <label style={{ fontSize: 10, margin: 0, minWidth: 'fit-content' }}>Coef. terreno:</label>
                    <select style={{ padding: '4px 6px', fontSize: 11, flex: 1 }} value={coefTer} onChange={e => setCoefTer(e.target.value)}>
                      <option value="0.25">0.25 — Fondo / sin uso</option>
                      <option value="0.33">0.33 — Irregular</option>
                      <option value="0.50">0.50 — Estándar</option>
                      <option value="0.60">0.60 — Subdivisible</option>
                      <option value="0.70">0.70 — Esquina / lote ind.</option>
                    </select>
                  </div>
                </div>
                <div className="f">
                  <label>Antigüedad (años)</label>
                  <input type="number" min="0" placeholder="0" value={antiguedad} onChange={e => setAntiguedad(e.target.value)} style={fieldErrors.antiguedad ? { borderColor: '#dc2626' } : undefined} />
                  {parseInt(antiguedad) >= 20 && (
                    <div style={{ marginTop: 4 }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', border: `1.5px solid ${reciclada ? 'var(--green)' : 'var(--line)'}`, borderRadius: 20, fontSize: 11.5, fontWeight: 600, color: reciclada ? 'var(--green)' : 'var(--mid)', background: reciclada ? 'var(--gl)' : '#fff', cursor: 'pointer' }}>
                        <input type="checkbox" style={{ display: 'none' }} checked={reciclada} onChange={e => setReciclada(e.target.checked)} />
                        🔧 Reciclada
                      </label>
                      <div className="hint">Suma valor si se renovaron instalaciones clave en los últimos 5 años</div>
                    </div>
                  )}
                </div>
                <div className="f"><label>Dormitorios</label><input type="number" min="0" placeholder="0" value={dorm} onChange={e => setDorm(e.target.value)} /></div>
                <div className="f"><label>Baños</label><input type="number" min="0" placeholder="0" value={ban} onChange={e => setBan(e.target.value)} /></div>
                <div className="f"><label>Cocheras</label><input type="number" min="0" placeholder="0" value={coch} onChange={e => setCoch(e.target.value)} /></div>
                <div className="f"><label>Piso (solo deptos)</label><input type="number" min="0" placeholder="PB=0" value={piso} onChange={e => setPiso(e.target.value)} /><div className="hint">+1.5% por cada piso</div></div>
                <div className="f"><label>Orientación</label>
                  <select value={ori} onChange={e => setOri(e.target.value)}>
                    <option value="1">No especificada</option>
                    <option value="1.05">Norte ☀️ (+5%)</option>
                    <option value="1.03">Noreste (+3%)</option>
                    <option value="1.03">Noroeste (+3%)</option>
                    <option value="1.0">Este (neutro)</option>
                    <option value="0.97">Oeste (-3%)</option>
                    <option value="0.97">Sudeste (-3%)</option>
                    <option value="0.95">Sur (-5%)</option>
                    <option value="0.95">Sudoeste (-5%)</option>
                  </select>
                </div>
                <div className="f">
                  <label>Estado</label>
                  <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                    <select style={{ flex: 1 }} value={resolveSelectValue(estado, estadoCustom, ESTADO_OPTIONS)} onChange={e => {
                      const val = e.target.value;
                      if (val === 'custom') {
                        setEstado(estadoCustom || estado);
                      } else {
                        setEstado(val);
                        setEstadoCustom('');
                      }
                    }}>
                      <option value="1.00">Excelente (1.00)</option>
                      <option value="0.97">Muy buena+ (0.97)</option>
                      <option value="0.95">Muy buena (0.95)</option>
                      <option value="0.92">Buena+ (0.92)</option>
                      <option value="0.90">Buena (0.90)</option>
                      <option value="0.87">Buena- (0.87)</option>
                      <option value="0.85">Regular+ (0.85)</option>
                      <option value="0.80">Regular (0.80)</option>
                      <option value="0.70">Mala (0.70)</option>
                      <option value="0.50">Pésima (0.50)</option>
                      <option value="custom">Personalizado...</option>
                    </select>
                    <input type="number" min="0.1" max="1.5" step="0.01" placeholder="0.90" style={{ width: 72, flexShrink: 0 }} value={estadoCustom} onChange={e => { setEstadoCustom(e.target.value); if (e.target.value) setEstado(e.target.value); }} />
                  </div>
                  <div className="hint">Coef. real aplicado: {fmtN(estado || 0.9, 2)}</div>
                </div>
                <div className="f"><label>Obsolescencia funcional</label>
                  <select value={obsf} onChange={e => setObsf(e.target.value)}>
                    <option value="1">Sin problemas (1.00)</option>
                    <option value="0.97">Distribución mejorable (0.97)</option>
                    <option value="0.93">Distribución mala (0.93)</option>
                    <option value="0.88">Obsoleta funcionalmente (0.88)</option>
                  </select>
                </div>
                <div className="f"><label>Servicios</label>
                  <input type="text" placeholder="Ej: Agua, Luz, Gas, Cloacas" list="vrc-sl" value={svc} onChange={e => setSvc(e.target.value)} />
                  <datalist id="vrc-sl">
                    {['Agua, Luz, Gas, Cloacas', 'Agua, Luz, Gas', 'Agua, Luz', 'Agua, Luz, Gas natural de red', 'Sin cloaca', 'Sin gas de red'].map(o => <option key={o} value={o} />)}
                  </datalist>
                </div>
              </div>
              <div className="sep" />
              <div className="g3">
                <div className="f">
                  <label>Frente real del lote (m)</label>
                  <div className="ui"><input type="number" min="0" step="0.1" placeholder="Ej: 8.66" value={frenteReal} onChange={e => setFrenteReal(e.target.value)} /><span className="un">m</span></div>
                  <div className="hint">Para cálculo Hoffman-Neill</div>
                </div>
                <div className="f">
                  <label>Frente tipo zona (m)</label>
                  <div className="ui"><input type="number" min="1" step="0.1" placeholder="10" value={frenteTipo} onChange={e => setFrenteTipo(e.target.value)} /><span className="un">m</span></div>
                  <div className="hint">Estándar del barrio (10 o 12m)</div>
                </div>
                <div className="f">
                  <label>Categoría constructiva del inmueble</label>
                  <select value={catProp} onChange={e => setCatProp(e.target.value)}>
                    <option value="0.85">Economía (0.85)</option>
                    <option value="1.00">Estándar (1.00)</option>
                    <option value="1.15">Confort (1.15)</option>
                    <option value="1.30">Lujo (1.30)</option>
                    <option value="1.50">Premium (1.50)</option>
                  </select>
                </div>
              </div>
              <div className="sep" />
              <div className="ct" style={{ marginBottom: '.5rem' }}>Amenities</div>
              <div className="am-grid">
                {AMENITIES_LIST.map(a => (
                  <label key={a.id} className={`am-c${amenitiesProp.includes(a.id) ? ' on' : ''}`} onClick={() => toggleAmenity(amenitiesProp, setAmenitiesProp, a.id)}>
                    {a.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Terreno */}
          {tipoInm === 't' && (
            <div className="card">
              <div className="ct">Características del Terreno</div>
              <div className="g3">
                <div className="f"><label>Sup. total (m²)</label><div className="ui"><input type="number" min="0" placeholder="0" value={tSup} onChange={e => setTSup(e.target.value)} style={fieldErrors.tSup ? { borderColor: '#dc2626' } : undefined} /><span className="un">m²</span></div></div>
                <div className="f"><label>Frente (m)</label><div className="ui"><input type="number" min="0" placeholder="0" value={tFr} onChange={e => setTFr(e.target.value)} /><span className="un">m</span></div></div>
                <div className="f"><label>Fondo (m)</label><div className="ui"><input type="number" min="0" placeholder="0" value={tFo} onChange={e => setTFo(e.target.value)} /><span className="un">m</span></div></div>
                <div className="f"><label>Topografía</label>
                  <select value={tTopo} onChange={e => setTTopo(e.target.value)}>
                    <option value="1.00">Plano (1.00)</option>
                    <option value="0.95">Leve pendiente (0.95)</option>
                    <option value="0.85">Pronunciada (0.85)</option>
                    <option value="0.70">Irregular (0.70)</option>
                  </select>
                </div>
                <div className="f"><label>Servicios disponibles</label>
                  <select value={tSvcCoef} onChange={e => setTSvcCoef(e.target.value)}>
                    <option value="1.00">Todos (agua, luz, gas, cloacas) · 1.00</option>
                    <option value="0.97">Sin gas (-3%) · 0.97</option>
                    <option value="0.94">Sin gas ni cloacas (-6%) · 0.94</option>
                    <option value="0.88">Solo luz / factibles (-12%) · 0.88</option>
                    <option value="0.82">Sin servicios (-18%) · 0.82</option>
                  </select>
                  <div className="hint">Coef. real aplicado: {tSvcCoef}</div>
                </div>
                <div className="f"><label>Servicios (texto descriptivo)</label>
                  <input type="text" placeholder="Ej: Agua, Luz, Gas, Cloacas" list="vrc-sl2" value={tSvc} onChange={e => setTSvc(e.target.value)} />
                  <datalist id="vrc-sl2">
                    {['Agua, Luz, Gas, Cloacas', 'Agua, Luz, Gas en vereda', 'Agua, Luz en vereda', 'Luz en vereda (sin agua de red)', 'Sin servicios', 'Factible de servicios'].map(o => <option key={o} value={o} />)}
                  </datalist>
                </div>
                <div className="f"><label>Forma del lote</label>
                  <select value={tForma} onChange={e => setTForma(e.target.value)}>
                    <option value="1.05">Esquina (+5%)</option>
                    <option value="1.00">Regular rectangular (1.00)</option>
                    <option value="0.97">Trapezoidal leve (-3%)</option>
                    <option value="0.93">Irregular (-7%)</option>
                    <option value="0.88">Muy irregular / interior (-12%)</option>
                  </select>
                </div>
                <div className="f"><label>Habilitación / Zonificación</label>
                  <select value={tHab} onChange={e => setTHab(e.target.value)}>
                    <option value="1.08">Comercial premium (+8%)</option>
                    <option value="1.03">Mixto residencial/comercial (+3%)</option>
                    <option value="1">Residencial urbano (1.00)</option>
                    <option value="0.9">Urbanizable (0.90)</option>
                    <option value="0.7">Rural (0.70)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Descripción */}
          <div className="card">
            <div className="ct">Descripción · Análisis IA</div>
            <div className="f"><textarea rows={4} placeholder="Describí el inmueble con tus palabras: estado real, entorno, barrio, accesos, servicios cercanos..." value={desc} onChange={e => setDesc(e.target.value)} /></div>
          </div>

          {/* Fotos */}
          <div className="card">
            <div className="ct">Fotos del Inmueble <span style={{ fontSize: 9.5, color: 'var(--mid)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: '.4rem' }}>La primera foto se muestra como imagen principal</span></div>
            <div className="pzone">
              <input type="file" accept="image/*" multiple onChange={e => addFotos('main', e.target.files)} />
              <div className="pz-i">📷</div>
              <div className="pz-t">Subí fotos del inmueble · JPG, PNG · La primera será la imagen destacada</div>
            </div>
            {renderFotos(fotosMain, 'main')}
          </div>

          {/* Precio aspiracional */}
          <div className="card">
            <div className="ct">Precio aspiracional del propietario</div>
            <div className="g2">
              <div className="f">
                <label>¿Cuánto quiere el propietario? (USD)</label>
                <div className="ui"><input type="number" min="0" step="500" placeholder="Ej: 180000" value={precioAsp} onChange={e => setPrecioAsp(e.target.value)} style={fieldErrors.precioAsp ? { borderColor: '#dc2626' } : undefined} /><span className="un">USD</span></div>
                <div className="hint">Dejalo vacío si no aplica o no lo sabés</div>
              </div>
              {resultado && precioAsp && (
                <div className="f" style={{ justifyContent: 'flex-end' }}>
                  <div style={{ background: 'var(--al)', border: '1.5px solid var(--amb)', borderRadius: 'var(--rs)', padding: '.75rem 1rem' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--amb)', marginBottom: '.2rem' }}>Brecha con el VRC</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--amb)', fontFamily: "'Cormorant Garamond', serif" }}>{fmt(Math.abs(parseFloat(precioAsp) - resultado.vrc))}</div>
                    <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2 }}>{(((parseFloat(precioAsp) / resultado.vrc) - 1) * 100).toFixed(1)}% {parseFloat(precioAsp) > resultado.vrc ? 'sobre' : 'bajo'} el VRC</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Nota */}
          <div className="card">
            <div className="ct">Nota del Agente</div>
            <div className="f"><textarea rows={3} placeholder="Criterio profesional, observaciones del mercado local, condiciones particulares..." value={nota} onChange={e => setNota(e.target.value)} /></div>
          </div>

          <div className="brow"><button className="btn btn-p" onClick={() => setStep(2)}>Siguiente: Comparable 1 →</button></div>
        </div>

        {/* ══ STEP 2: COMPARABLE 1 ══ */}
        <div className={`sec${step === 2 ? ' act' : ''}`}>
          {renderCompForm(comp1, setComp1, 'Primer inmueble de referencia', 'blue', 1)}
          <div className="brow">
            <button className="btn btn-s" onClick={() => setStep(1)}>← Atrás</button>
            <button className="btn btn-p" onClick={() => setStep(3)}>Siguiente: Comparable 2 →</button>
          </div>
        </div>

        {/* ══ STEP 3: COMPARABLE 2 ══ */}
        <div className={`sec${step === 3 ? ' act' : ''}`}>
          {renderCompForm(comp2, setComp2, 'Segundo inmueble de referencia', 'wine', 2)}
          <div className="brow">
            <button className="btn btn-s" onClick={() => setStep(2)}>← Atrás</button>
            {!showComp3 && (
              <button className="btn btn-ol" onClick={() => setShowComp3(true)}>+ Agregar Comparable 3</button>
            )}
            <button className="btn btn-p" onClick={() => setStep(showComp3 ? 4 : 5)}>Siguiente: {showComp3 ? 'Comparable 3' : 'VRC'} →</button>
          </div>
        </div>

        {/* ══ STEP 4: COMPARABLE 3 (opcional) ══ */}
        {showComp3 && (
          <div className={`sec${step === 4 ? ' act' : ''}`}>
            {renderCompForm(comp3, setComp3, 'Tercer inmueble de referencia (opcional)', 'green', 3)}
            <div className="brow">
              <button className="btn btn-s" onClick={() => setStep(3)}>← Atrás</button>
              <button className="btn btn-p" onClick={() => setStep(5)}>Siguiente: VRC →</button>
            </div>
          </div>
        )}

        {/* ══ STEP 5: CALCULAR VRC ══ */}
        <div className={`sec${step === 5 ? ' act' : ''}`}>
          <div className="card">
            <div className="ct">Calcular VRC</div>
            <div style={{ marginBottom: '1.1rem', fontSize: 13, color: 'var(--mid)', lineHeight: 1.7 }}>
              Revisá los datos cargados y presioná el botón para calcular el Valor Real Competitivo.
            </div>

            {/* Resumen datos */}
            <div className="sgrid" style={{ marginBottom: '1.1rem' }}>
              <div className="sc">
                <div className="lb">Inmueble</div>
                <div className="vl" style={{ fontSize: '1rem' }}>{tipo || '—'}</div>
                <div className="sb2">{dir || '—'}</div>
              </div>
              {(supCub || tSup) && <div className="sc">
                <div className="lb">Superficie</div>
                <div className="vl">{fmtN(parseFloat(supCub || tSup))} m²</div>
                <div className="sb2">cubierta{tSup ? ' total' : ''}</div>
              </div>}
              <div className="sc w">
                <div className="lb">Comparables</div>
                <div className="vl">{[comp1, comp2, ...(showComp3 ? [comp3] : [])].filter(c => parseFloat(c.precio) > 0).length}</div>
                <div className="sb2">cargados</div>
              </div>
              <div className="sc">
                <div className="lb">Agente</div>
                <div className="vl" style={{ fontSize: '1rem' }}>{agente || '—'}</div>
                <div className="sb2">{fecha}</div>
              </div>
            </div>

            {/* Ajuste manual */}
            <div style={{ background: 'var(--stone)', borderRadius: 9, padding: '1rem', marginBottom: '1.1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', marginBottom: '.625rem' }}>
                <input type="checkbox" checked={manualMode} onChange={e => setManualMode(e.target.checked)} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>🎚️ Ajustar VRC manualmente</span>
              </label>
              {manualMode && (
                <div className="f" style={{ maxWidth: 280 }}>
                  <label>VRC manual (USD)</label>
                  <div className="ui"><input type="number" min="0" step="500" placeholder="Ej: 185000" value={manualVRC} onChange={e => setManualVRC(e.target.value)} /><span className="un">USD</span></div>
                  <div className="hint">Sobreescribe el cálculo automático · úsalo con criterio profesional</div>
                </div>
              )}
            </div>

            <div className="brow" style={{ justifyContent: 'center' }}>
              <button className="btn btn-s" onClick={() => setStep(showComp3 ? 4 : 3)}>← Atrás</button>
              <button className="btn btn-w btn-lg" onClick={handleCalc}>
                {loading ? <><span className="spin-s" />Calculando…</> : '🧮 Calcular VRC'}
              </button>
            </div>
          </div>

          {/* Alertas de validación del cálculo */}
          {calcError && (
            <div
              role="alert"
              style={{
                marginTop: '.75rem',
                padding: '.65rem .85rem',
                background: '#fde2e2',
                color: '#9b1c1c',
                border: '1px solid #f5c2c2',
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              ❌ <strong>No se pudo calcular el VRC{calcErrorsList.length > 0 ? `: ${calcErrorsList.length} error(es)` : ''}.</strong>
              {calcErrorsList.length > 0 ? (
                <ul style={{ margin: '.4rem 0 0 1.2rem', padding: 0 }}>
                  {calcErrorsList.map((err, i) => (
                    <li key={i}><strong>{getVRCFieldLabel(err.field)}:</strong> {err.message}</li>
                  ))}
                </ul>
              ) : (
                <span> {calcError}</span>
              )}
            </div>
          )}
          {calcWarnings.length > 0 && !calcError && (
            <div
              role="status"
              style={{
                marginTop: '.75rem',
                padding: '.65rem .85rem',
                background: '#fef3c7',
                color: '#854d0e',
                border: '1px solid #fde68a',
                borderRadius: 8,
                fontSize: 12.5,
                lineHeight: 1.4,
              }}
            >
              ⚠️ <strong>Advertencias:</strong>
              <ul style={{ margin: '.25rem 0 0 1.1rem', padding: 0 }}>
                {calcWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {loading && (
            <div className="loading on">
              <div className="spin-lg" />
              <div className="lt">Calculando Valor Real Competitivo…</div>
              <div className="ls">Aplicando coeficientes y comparables</div>
            </div>
          )}
        </div>

        {/* RESULTADO */}
        {showResult && (
          <>
            {/* CONTROLES — visibles en pantalla en ambas vistas, ocultos en el PDF */}
            <div className="vrc-controls-bar no-pdf">
              <div className="vtg">
                <button className={`vt${viewMode === 'agent' ? ' act' : ''}`} onClick={() => setViewMode('agent')}>👔 Vista Agente</button>
                <button className={`vt${viewMode === 'prop' ? ' act' : ''}`} onClick={() => setViewMode('prop')}>👤 Vista Propietario</button>
              </div>
              <div className="vrc-controls-actions">
                <button className="btn btn-s" onClick={() => { setShowResult(false); setStep(5); }}>✏️ Editar</button>
                <button className="btn btn-ol" onClick={() => { setManualMode(true); setStep(5); }}>🎚️ Ajustar VRC</button>
                <button className="btn btn-w" onClick={() => generatePDF('agent')}>⬇️ PDF Técnico</button>
                <button className="btn btn-w" onClick={() => generatePDF('prop')}>👤 PDF Propietario</button>
                <button
                  className="btn btn-p"
                  onClick={handleSave}
                  disabled={saving}
                  title={isEditMode ? 'Actualizar VRC en el servidor' : 'Guardar VRC en el servidor'}
                >
                  {saving ? <><span className="spin-s" /> Guardando…</> : (isEditMode ? '💾 Actualizar VRC' : '💾 Guardar VRC')}
                </button>
              </div>
            </div>
            {saveError && (
              <div className="no-pdf" style={{ margin: '.4rem 0 1rem', padding: '.5rem .75rem', background: '#fde2e2', color: '#9b1c1c', borderRadius: 6, fontSize: 13 }}>
                ❌ {saveError}
              </div>
            )}
            {savedId && !saveError && (
              <div className="no-pdf" style={{ margin: '.4rem 0 1rem', padding: '.5rem .75rem', background: '#d1fae5', color: '#065f46', borderRadius: 6, fontSize: 13 }}>
                ✅ VRC guardado. Redirigiendo al detalle…
              </div>
            )}

            {viewMode === 'agent' ? renderResultado() : renderResultadoPropietario()}
          </>
        )}

      </div>
    </div>
  );
}
